import { useState, useEffect, useCallback } from 'react'
import { api } from '../lib/api'
import toast from 'react-hot-toast'
import { Badge, MarketBadge, Modal, Input, Select, Textarea, Empty, LinkCell, HtmlCell, UrlCopyCell } from '../components/UI'
import { ViewFunnel, ViewCreative, ViewAdCopy, ViewScheduling } from '../components/ViewModal'
import { MARKETS, FUNNEL_STATUS, CREATIVE_STATUS, AD_COPY_STATUS, CAMPAIGN_STATUS, AD_ACCOUNTS, WHO_CREATES, EDITORS, APPROVERS } from '../lib/constants'
import { useAuth } from '../hooks/useAuth'
import { can } from '../components/Sidebar'
import { useRowSelection } from '../hooks/useRowSelection'
import { usePersistentSort } from '../hooks/usePersistentSort'
import { useResizableColumns } from '../hooks/useResizableColumns'
import { useColumns, ColumnPicker } from '../components/ColumnPicker'
import { DensityToggle } from '../components/DensityToggle'
import { useTableFilter, FilterButton } from '../components/TableFilter'

function ActionBtns({ onView, onEdit, onDel, canView=true, canEdit=true, canDel=false }) {
  return (
    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:4,width:148}}>
      {canView && <button className="btn btn-secondary" style={{padding:'4px 6px',fontSize:11,whiteSpace:'nowrap',justifyContent:'center'}} onClick={onView}>👁 View</button>}
      {canEdit && <button className="btn btn-secondary" style={{padding:'4px 6px',fontSize:11,whiteSpace:'nowrap',justifyContent:'center'}} onClick={onEdit}>✏️ Edit</button>}
      {canDel && onDel && <button className="btn btn-danger" style={{padding:'4px 6px',fontSize:11,whiteSpace:'nowrap',justifyContent:'center'}} onClick={onDel}>🗑 Del</button>}
    </div>
  )
}

function useSection(section, market) {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const load = useCallback(async () => {
    setLoading(true)
    try { setRows(await api.getSection(section, market)) }
    catch (err) { toast.error(err.message) }
    setLoading(false)
  }, [section, market])
  useEffect(() => { load() }, [load])
  // Patch a single row in place — avoids a full refetch (and losing scroll position)
  // after an inline edit. Server response is merged so computed fields stay accurate.
  const patchRow = useCallback((id, patch) => {
    setRows(prev => prev.map(r => r.id === id ? { ...r, ...patch } : r))
  }, [])
  // Save inline without reloading the table. Falls back to a full reload on failure
  // so the UI can never drift from the server.
  // Re-fetch rows WITHOUT flipping `loading` — the table never unmounts, so scroll
  // position and the row you're working on stay put. Used for changes that have
  // server-side side effects (approvals, work status) where a real refresh is required.
  const softReload = useCallback(async () => {
    // Capture the scroll position of the visible table before swapping data, then
    // restore it after React paints — otherwise the browser clamps scrollTop while
    // the row set is briefly different and the view jumps to the top.
    const wrap = document.querySelector('.table-wrap')
    const top = wrap ? wrap.scrollTop : 0
    const left = wrap ? wrap.scrollLeft : 0
    try {
      const fresh = await api.getSection(section, market)
      setRows(fresh)
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const w = document.querySelector('.table-wrap')
          if (w) { w.scrollTop = top; w.scrollLeft = left }
        })
      })
    } catch (err) { toast.error(err.message) }
  }, [section, market])
  const saveInline = useCallback(async (id, body) => {
    try {
      const updated = await api.updateSection(section, id, body)
      setRows(prev => prev.map(r => r.id === id ? { ...r, ...(updated && updated.id ? updated : body) } : r))
      return true
    } catch (err) {
      toast.error(err.message)
      load()
      return false
    }
  }, [section, load])
  return { rows, loading, reload: load, softReload, patchRow, saveInline }
}

const isNewlyAssigned = (assignedAt) => {
  if (!assignedAt) return false
  return (Date.now() - new Date(assignedAt).getTime()) < 48 * 3600 * 1000
}
function useUsers() {
  const [users, setUsers] = useState([])
  useEffect(() => { api.getAssignableUsers().then(setUsers).catch(() => {}) }, [])
  return users
}
function useProducts() {
  const [products, setProducts] = useState([])
  useEffect(() => { api.getProducts().then(setProducts).catch(() => {}) }, [])
  return products
}

function useActiveMarkets() {
  const [markets, setMarkets] = useState([])
  useEffect(() => {
    api.getSettings().then(s => {
      setMarkets(s.active_markets || ['SE','NL-BE','UK','US','AU','NO','GE','FR','DK','FI','CA'])
    }).catch(() => {
      setMarkets(['SE','NL-BE','UK','US','AU','NO','GE','FR','DK','FI','CA'])
    })
  }, [])
  return markets
}


const CHECKLIST_ITEMS = [
  {
    section: 'Funnel Setup & Naming',
    icon: '🏗️',
    items: [
      { id: 'naming', label: 'Follow the naming convention exactly as listed in the "Funnel Name" column' },
      { id: 'domain', label: 'Use the correct domain for the target market when creating the funnel' },
      { id: 'path', label: 'Ensure the funnel path is updated correctly based on the market' },
    ],
    note: 'Domains by Market: SE → biomevexa.com | NL-BE → elurelia.com | NO → elurique.com | DK → skinavie.com | FI → lumiellea.com'
  },
  {
    section: 'WeTracked Setup',
    icon: '📊',
    items: [
      { id: 'wt_codes', label: 'Funnel includes both custom codes under Funnel Settings for WeTracked integration' },
      { id: 'wt_automation', label: 'WeTracked automation is properly configured in Automation Settings' },
      { id: 'wt_name', label: 'Added to WeTracked.io using the exact Funnel Name' },
      { id: 'wt_timezone', label: 'Correct timezone selected for the target market in WeTracked' },
      { id: 'wt_pixel', label: 'Correct market pixel selected in WeTracked (SE→Sweden, NL-BE→NL/BE, FI→Finland, DK→Denmark)' },
      { id: 'wt_verified', label: 'WeTracked tested and confirmed connected and tracking correctly' },
    ]
  },
  {
    section: 'Shopify Product Setup',
    icon: '🛒',
    items: [
      { id: 'shopify_store', label: 'Product connected to the correct Shopify store for the target market' },
      { id: 'shopify_language', label: 'Product added to the appropriate Shopify store based on target market and language' },
      { id: 'shopify_upsell', label: 'Upsells created using Sweet Upsell or AfterSell' },
    ]
  },
  {
    section: 'Final Review & Audit',
    icon: '✅',
    items: [
      { id: 'audit_done', label: 'Full audit completed after funnel setup' },
      { id: 'audit_language', label: 'No language issues or untranslated text found' },
      { id: 'audit_links', label: 'No broken links' },
      { id: 'audit_domains', label: 'Correct domains and paths verified' },
      { id: 'audit_tracking', label: 'Tracking and automation working correctly' },
      { id: 'audit_formatting', label: 'No general formatting or functionality problems' },
    ]
  }
]

function useSettingsLists() {
  const [lists, setLists] = useState({ editors:[], approvers:[], ad_accounts:[], who_creates:[], currency_rates:{} })
  useEffect(() => {
    api.getSettings().then(s => setLists({
      editors: s.editors || [],
      approvers: s.approvers || [],
      ad_accounts: s.ad_accounts || [],
      who_creates: s.who_creates || [],
      currency_rates: s.currency_rates || {},
    })).catch(() => {})
  }, [])
  return lists
}

// ── FUNNEL BUILDING ──────────────────────────────────────────
function CopyableText({ text }) {
  const [copied, setCopied] = useState(false)
  if (!text) return <span>—</span>
  const copy = (e) => {
    e.stopPropagation()
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 1500)
    })
  }
  return (
    <div style={{width:'100%',display:'block',position:'relative'}}>
      <div style={{whiteSpace:'pre-wrap',fontSize:12,lineHeight:1.5,maxHeight:180,overflow:'auto'}}>{text}</div>
      <button onClick={copy} style={{marginTop:4,fontSize:10,fontWeight:600,padding:'2px 8px',borderRadius:5,border:'1px solid var(--border2)',background:copied?'var(--accent3)':'var(--bg3)',color:copied?'#000':'var(--accent)',cursor:'pointer'}}>{copied?'✓ Copied':'📋 Copy'}</button>
    </div>
  )
}
export function FunnelBuilding({ market }) {
  const { user } = useAuth()
  const isOwner = user?.role === 'owner'
  const perms = (typeof user?.permissions === 'object' && user?.permissions) ? user.permissions : {}
  const cFunnel = (a) => can(perms, 'funnel', a, isOwner)
  const fUsers = useUsers()
  const canAssign = isOwner || perms?.can_approve === true
  const canDeadline = isOwner || perms?.can_approve === true
  const myMarket = isOwner ? market : user?.assigned_market
  const { rows, loading, reload, softReload, saveInline } = useSection('funnel', myMarket)
  const { rowProps } = useRowSelection('funnel')
  const products = useProducts()
  const activeMarkets = useActiveMarkets()
  const FUNNEL_FILTERS = [
    { key:'market', label:'Market', options: activeMarkets.map(m=>({value:m,label:m})) },
    { key:'funnel_status', label:'Work Status', options: FUNNEL_STATUS.map(x=>({value:x,label:x})) },
    { key:'approval', label:'Approval', options:[{value:'Pending',label:'Pending'},{value:'Approved',label:'Approved'},{value:'Updates Needed',label:'Updates Needed'}] },
  ]
  const funnelFilter = useTableFilter('funnel', FUNNEL_FILTERS)
  const settingsLists = useSettingsLists()
  const { sortCol, setSortCol, sortDir, setSortDir } = usePersistentSort('funnel', 'product_id')
  const [modal, setModal] = useState(null)
  const [viewRow, setViewRow] = useState(null)
  const [form, setForm] = useState({})
  const [saving, setSaving] = useState(false)
  const [auditRow, setAuditRow] = useState(null)
  const [revisionPrompt, setRevisionPrompt] = useState(null)
  const [revisionText, setRevisionText] = useState('')
  const [auditLoading, setAuditLoading] = useState(false)
  const [auditText, setAuditText] = useState('')
  const [manualContent, setManualContent] = useState('')
  const [showManual, setShowManual] = useState(false)
  const [wetrackedRow, setWetrackedRow] = useState(null)
  const [wetrackedLoading, setWetrackedLoading] = useState(false)
  const [wetrackedResult, setWetrackedResult] = useState(null)
  const [checklistRow, setChecklistRow] = useState(null)
  const [checklistData, setChecklistData] = useState({})
  const [checklistSaving, setChecklistSaving] = useState(false)
  const { cols: colVis, isVisible, toggle: colToggle, reset: colReset } = useColumns('funnel')
  const { getThProps, getTdProps, Resizer } = useResizableColumns('funnel')

  function openEdit(r) { setForm(r); setModal(r) }
  function openAdd() { setForm({ market: myMarket||'SE', funnel_status:'' }); setModal('add') }

  async function save() {
    setSaving(true)
    try {
      if (modal === 'add') await api.createSection('funnel', form)
      else await api.updateSection('funnel', modal.id, form)
      toast.success('Saved!'); setModal(null); softReload()
    } catch (err) { toast.error(err.message) }
    setSaving(false)
  }

  async function delRow(r) {
    if (!window.confirm(`Delete funnel record for ${r.product_name} — ${r.market}? This cannot be undone.`)) return
    try {
      await api.deleteSection('funnel', r.id)
      toast.success('Record deleted')
      softReload()
    } catch (err) { toast.error(err.message) }
  }

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }))

  function openAudit(r) {
    setAuditRow(r)
    setAuditText(r.audit_report || '')
    setManualContent('')
    setShowManual(false)
  }

  async function runAudit(contentOverride) {
    if (!auditRow) return
    setAuditLoading(true)
    let pageContent = contentOverride || ''

    if (!pageContent) {
      // Try to fetch the funnel link
      if (!auditRow.funnel_link) { toast.error('No funnel link set for this record'); setAuditLoading(false); return }
      try {
        const token = localStorage.getItem('adops_token')
        const r = await fetch('/api/content/fetch-url-rendered', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ url: auditRow.funnel_link })
        })
        const d = await r.json()
        // Separate raw fetch just to discover policy links (rendered endpoint has none)
        let policyLinksSrc = {}
        try {
          const rawR = await fetch('/api/content/fetch-url', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ url: auditRow.funnel_link })
          })
          const rawD = await rawR.json()
          policyLinksSrc = rawD.policyLinks || {}
        } catch {}
        if (d.success && d.extracted) {
          const rendered = async (murl, device) => {
            try {
              const rr = await fetch('/api/content/fetch-url-rendered', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ url: murl, device })
              })
              const rd = await rr.json()
              return (rd.success && rd.extracted) ? rd.extracted : ''
            } catch { return '' }
          }
          // ---- DESKTOP ----
          const salesD = d.extracted
          const advD = auditRow.advertorial_link ? await rendered(auditRow.advertorial_link, 'desktop') : ''
          // ---- MOBILE ----
          const salesM = await rendered(auditRow.funnel_link, 'mobile')
          const advM = auditRow.advertorial_link ? await rendered(auditRow.advertorial_link, 'mobile') : ''
          // ---- POLICY (desktop only) ----
          const pl = policyLinksSrc || {}
          const fetchPolicy = async (purl, marker) => {
            if (!purl) return ''
            try {
              const pr = await fetch('/api/content/fetch-url', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ url: purl })
              })
              const pd = await pr.json()
              if (pd.success && pd.extracted) return '===== ' + marker + ' =====\nURL: ' + purl + '\n\n' + pd.extracted
            } catch {}
            return ''
          }
          const privBlock = await fetchPolicy(pl.privacy, 'PRIVACY POLICY')
          const discBlock = await fetchPolicy(pl.disclaimer, 'DISCLAIMER')
          const policyBlock = [privBlock, discBlock].filter(Boolean).join('\n\n\n')

          // Assemble: DESKTOP group, then MOBILE group
          const desktopParts = []
          if (advD) desktopParts.push('===== ADVERTORIAL PAGE =====\nURL: ' + auditRow.advertorial_link + '\n\n' + advD)
          desktopParts.push('===== SALES PAGE =====\nURL: ' + auditRow.funnel_link + '\n\n' + salesD)
          if (policyBlock) desktopParts.push(policyBlock)

          const mobileParts = []
          if (advM) mobileParts.push('===== ADVERTORIAL PAGE (MOBILE) =====\nURL: ' + auditRow.advertorial_link + '\n\n' + advM)
          if (salesM) mobileParts.push('===== SALES PAGE (MOBILE) =====\nURL: ' + auditRow.funnel_link + '\n\n' + salesM)

          const desktopContent = '########## DESKTOP VERSION ##########\n\n' + desktopParts.join('\n\n\n')
          const mobileContent = mobileParts.length ? '\n\n\n########## MOBILE VERSION ##########\n\n' + mobileParts.join('\n\n\n') : ''
          pageContent = desktopContent + mobileContent
        } else {
          setShowManual(true)
          setAuditLoading(false)
          toast.error('Could not fetch page. Please paste the content manually.')
          return
        }
      } catch (err) {
        setShowManual(true)
        setAuditLoading(false)
        toast.error('Fetch failed. Please paste the content manually.')
        return
      }
    }

    const countryMap = {'SE':'Sweden','UK':'United Kingdom','NL-BE':'Netherlands/Belgium','GE':'Germany','FR':'France','NO':'Norway','DK':'Denmark','FI':'Finland','US':'United States','AU':'Australia','CA':'Canada'}
    const langMap = {'SE':'Swedish','UK':'English','NL-BE':'Dutch','GE':'German','FR':'French','NO':'Norwegian','DK':'Danish','FI':'Finnish','US':'English','AU':'English','CA':'English'}
    const country = countryMap[auditRow.market] || auditRow.market
    const language = langMap[auditRow.market] || 'English'

    try {
      const token = localStorage.getItem('adops_token')
      const r = await fetch('/api/content/proxy-claude', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          system: `You are an expert proofreader for eCommerce pages. You check three things only: whether a page is fully translated into the target language, whether it is correctly localized for the target market, and whether the brand and product names are consistent. Write your report in English, but quote any problem text in its original language.`,
          user: `Audit this page content.
BRAND: ${auditRow.brand_name || ''}
PRODUCT: ${auditRow.product_name || ''}
MARKET: ${auditRow.market} (${country})
EXPECTED LANGUAGE: ${language}

PAGE CONTENT:
${pageContent.length > 30000 ? pageContent.substring(0, 30000) + '\n\n[NOTE: Content truncated at 30,000 characters due to length]' : pageContent}

IMPORTANT: The page content is scraped from a live page and may contain leftover technical artifacts — CSS values (e.g. "1024px", "max-width", "@media"), code fragments, HTML tags, or layout keywords. These are NOT content and must be COMPLETELY IGNORED. Only audit real, human-facing text (headlines, body, buttons, testimonials, FAQs). Never flag technical/code/pixel/CSS text.

Check ONLY these three things:
1. TRANSLATION — Is the ENTIRE page written in ${language}? Flag any text still in English or another language, or left untranslated.
2. LOCALIZATION — Flag anything that does not fit ${country}: wrong currency, foreign names or places, or references to the wrong country.
3. BRAND NAME — The brand name is "${auditRow.brand_name || ''}". Flag any place where the brand name is misspelled, inconsistent, or where a DIFFERENT product/brand name appears (e.g. a competitor name). The brand name "${auditRow.brand_name || ''}" should be used consistently everywhere the product is named. Do NOT check for any product name other than the brand name.

OUTPUT (FOLLOW EXACTLY):
Plain text only. No markdown, no summaries, no scores, no explanations.
The content is split into two top-level parts marked '########## DESKTOP VERSION ##########' and '########## MOBILE VERSION ##########'. Audit them as TWO COMPLETELY SEPARATE reports. First output the full DESKTOP report, then the full MOBILE report. Within each part, audit each page (ADVERTORIAL, SALES, and for desktop also PRIVACY POLICY + DISCLAIMER) separately. Skip any page whose marker is absent. If there is no MOBILE VERSION in the content, omit the mobile report entirely.
For PRIVACY POLICY and DISCLAIMER pages: these are legal pages. Do NOT check translation quality. ONLY check that the brand name "${auditRow.brand_name || ''}" is used correctly and consistently, and that the website domain/URL shown matches the sales page domain (flag any wrong domain, placeholder domain, or a different brand's domain).
Use exactly this format. Output the DESKTOP report first, then the MOBILE report.

██████████  DESKTOP VERSION  ██████████

═════════  ADVERTORIAL PAGE  ═════════

[TRANSLATION]
- ...
[LOCALIZATION]
- ...
[BRAND NAME]
- ...

═════════  SALES PAGE  ═════════

[TRANSLATION]
- ...
[LOCALIZATION]
- ...
[BRAND NAME]
- ...

═════════  PRIVACY POLICY  ═════════

[BRAND & DOMAIN]
- ...

═════════  DISCLAIMER  ═════════

[BRAND & DOMAIN]
- ...
██████████  MOBILE VERSION  ██████████

═════════  ADVERTORIAL PAGE  ═════════

[TRANSLATION]
- ...
[LOCALIZATION]
- ...
[BRAND NAME]
- ...

═════════  SALES PAGE  ═════════

[TRANSLATION]
- ...
[LOCALIZATION]
- ...
[BRAND NAME]
- ...

If a category has no issues, write '- No issues found'. Quote the problematic word or phrase when possible. Omit the entire MOBILE VERSION report if no mobile content is present.`,
          max_tokens: 4000
        })
      })
      const d = await r.json()
      if (d.error) throw new Error(d.error)
      setAuditText(d.text)
    } catch (err) { toast.error('Audit failed: ' + err.message) }
    setAuditLoading(false)
  }

  async function saveAudit() {
    if (!auditRow || !auditText) return
    try {
      const token = localStorage.getItem('adops_token')
      await fetch('/api/content/save-audit-report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ funnel_id: auditRow.id, audit_report: auditText })
      })
      toast.success('Audit report saved!')
      setAuditRow(null)
      softReload()
    } catch (err) { toast.error(err.message) }
  }

  async function clearAudit() {
    if (!auditRow) return
    if (!window.confirm('Clear audit report for this record? This cannot be undone.')) return
    try {
      const token = localStorage.getItem('adops_token')
      await fetch('/api/content/save-audit-report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ funnel_id: auditRow.id, audit_report: '' })
      })
      setAuditText('')
      toast.success('Audit report cleared')
      softReload()
    } catch (err) { toast.error(err.message) }
  }

  function openChecklist(r) {
    setChecklistRow(r)
    try {
      const cl = typeof r.checklist === 'string' ? JSON.parse(r.checklist || '{}') : (r.checklist || {})
      setChecklistData(cl)
    } catch { setChecklistData({}) }
  }

  async function saveChecklist() {
    if (!checklistRow) return
    setChecklistSaving(true)
    try {
      await api.updateSection('funnel', checklistRow.id, { checklist: JSON.stringify(checklistData) })
      toast.success('Checklist saved!')
      setChecklistRow(null)
      softReload()
    } catch (err) { toast.error(err.message) }
    setChecklistSaving(false)
  }

  function toggleCheck(id) {
    setChecklistData(prev => ({ ...prev, [id]: !prev[id] }))
  }

  async function verifyWetracked(r) {
    if (!r.funnel_link) { toast.error('No funnel link set for this record'); return }
    setWetrackedRow(r)
    setWetrackedResult(null)
    setWetrackedLoading(true)
    try {
      const token = localStorage.getItem('adops_token')
      const resp = await fetch('/api/wetracked/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ url: r.funnel_link })
      })
      const d = await resp.json()
      setWetrackedResult(d)
    } catch (err) { setWetrackedResult({ error: err.message, message: '❌ Request failed: ' + err.message }) }
    setWetrackedLoading(false)
  }

  const cols = [
    { key:'market', label:'Market', render: r => <MarketBadge market={r.market} /> },
    { key:'product_id', label:'Product ID', render: r => <strong>{r.p_product_id||r.product_id}</strong> },
    { key:'brand_name', label:'Brand Name', render: r => <strong>{r.brand_name||'—'}</strong> },
    { key:'competitor_product_url', label:'Competitor Link', render: r => <LinkCell url={r.competitor_product_url} /> },
    { key:'competitor_advertorial_url', label:'Comp Advertorial', render: r => <LinkCell url={r.competitor_advertorial_url} /> },
    { key:'comp_adv_html', label:'Comp Adv HTML', render: r => <HtmlCell has={!!r.comp_adv_html} id={r.p_row_id} which="adv" canEdit={false} /> },
    { key:'comp_sales_html', label:'Comp Sales HTML', render: r => <HtmlCell has={!!r.comp_sales_html} id={r.p_row_id} which="sales" canEdit={false} /> },
    { key:'competitor_page_ad_url', label:'Comp Page Ad', render: r => <LinkCell url={r.competitor_page_ad_url} /> },
    { key:'aliexpress_link', label:'Sourcing Link', render: r => <LinkCell url={r.aliexpress_link} /> },
    { key:'offer_to_use', label:'Offer to Use', render: r => r.offer_to_use||'—' },
    { key:'funnel_link', label:'Funnel Link', render: r => <LinkCell url={r.funnel_link} /> },
    { key:'advertorial_link', label:'Advertorial Link', render: r => <LinkCell url={r.advertorial_link} /> },
    { key:'content_ready', label:'Content', render: r => {
      const ready = r.content_ready === true
      if (!isOwner) return <span style={{fontSize:11,fontWeight:700,color:ready?'var(--accent3)':'#F59E0B',whiteSpace:'nowrap'}}>{ready?'✓ Ready':'⏳ Awaiting'}</span>
      return <select value={ready?'yes':'no'} onClick={e=>e.stopPropagation()}
        onChange={async e=>{ try{ await saveInline(r.id, { content_ready: e.target.value==='yes' }) }catch(err){toast.error(err.message)} }}
        style={{background:'var(--bg3)',border:'1px solid var(--border2)',borderRadius:6,color:ready?'var(--accent3)':'#F59E0B',padding:'4px 8px',fontSize:11,fontWeight:700,cursor:'pointer',outline:'none',width:'100%'}}>
        <option value="no">⏳ Awaiting</option><option value="yes">✓ Ready</option>
      </select>
    }},
    { key:'funnel_name', label:'Funnel Name', render: r => {
      const pid = r.p_product_id || r.product_id
      const brand = (r.brand_name||'').replace(/™|®/g,'').trim().replace(/\s+/g,'-')
      const auto = [pid, brand, r.market].filter(Boolean).join('-')
      return <span style={{fontFamily:'DM Mono,monospace',fontSize:11,color:'var(--accent3)'}}>{auto}</span>
    }},
    { key:'deadline', label:'Deadline', render: r => {
      const overdue = r.deadline && r.funnel_status !== 'Ready' && new Date(r.deadline) < new Date(new Date().toISOString().slice(0,10))
      return <span style={{display:'flex',alignItems:'center',gap:4}}>
        <input type="date" value={r.deadline?r.deadline.slice(0,10):''} disabled={!canDeadline}
          onChange={async e => { try { await saveInline(r.id, { deadline: e.target.value }) } catch(err){ toast.error(err.message) } }}
          onClick={e=>e.stopPropagation()}
          style={{background:'var(--bg3)',border:'1px solid '+(overdue?'#EF4444':'var(--border2)'),borderRadius:6,color:overdue?'#EF4444':'var(--text)',padding:'3px 6px',fontSize:11,outline:'none'}} />
        {overdue?'⚠':''}
      </span>
    }},
    { key:'funnel_status', label:'Work Status', render: r => (
      <select
        value={r.funnel_status||'Not Started'}
        onChange={async e => {
          const val = e.target.value
          try {
            await api.updateSection('funnel', r.id, { funnel_status: val })
            softReload()
          } catch(err) { toast.error(err.message) }
        }}
        onClick={e => e.stopPropagation()}
        style={{
          background:'var(--bg3)',border:'1px solid var(--border2)',borderRadius:6,
          color: r.funnel_status==='Updates Required'?'#F59E0B':'var(--text)',
          padding:'4px 8px',fontSize:11,fontWeight:600,cursor:'pointer',
          outline:'none',width:'100%',fontFamily:'DM Sans,sans-serif'
        }}
      >
        {['Not Started','In Progress','Proofreading','Ready','Updates Required','On Hold','Cancelled'].map(s => (
          <option key={s} value={s}>{s}</option>
        ))}
      </select>
    )},
    { key:'assigned_to', label:'Assignee', render: r => canAssign ? (<>
      <select value={r.assigned_to||''} onChange={async e => { try { await api.updateSection('funnel', r.id, { assigned_to: e.target.value || null }); softReload() } catch(err){ toast.error(err.message) } }}
        onClick={e=>e.stopPropagation()} style={{background:'var(--bg3)',border:'1px solid var(--border2)',borderRadius:6,color:'var(--text)',padding:'4px 8px',fontSize:11,cursor:'pointer',outline:'none',width:'100%'}}>
        <option value="">— Unassigned —</option>
        {fUsers.map(u=><option key={u.id} value={u.id}>{u.full_name}</option>)}
      </select>{isNewlyAssigned(r.assigned_at) && <span style={{marginLeft:4,fontSize:9,fontWeight:700,color:'#F59E0B'}}>● NEW</span>}</>
    ) : <span style={{fontSize:11,color:r.assignee_name?'var(--text2)':'var(--text3)'}}>{r.assignee_name||'—'}{isNewlyAssigned(r.assigned_at) && <span style={{marginLeft:6,fontSize:9,fontWeight:700,color:'#F59E0B'}}>● NEW</span>}</span> },
    { key:'completed', label:'Completed', render: r => (r.completed_by || r.date_completed)
      ? <span style={{fontSize:11,color:'var(--text2)'}}>{r.completed_by||'—'}{r.date_completed ? <span style={{color:'var(--text3)',display:'block',fontSize:10}}>{new Date(r.date_completed).toLocaleDateString()}</span> : null}</span>
      : <span style={{color:'var(--text3)'}}>—</span>
    },
    { key:'approval', label:'Approval', render: r => {
      const canApp = isOwner || perms.can_approve === true
      const colorMap = { 'Approved':'var(--accent3)', 'Updates Needed':'#EF4444', 'Pending':'var(--text3)' }
      if (!canApp) {
        return <span style={{fontSize:11,fontWeight:700,color:colorMap[r.approval]||'var(--text3)'}}>{r.approval||'—'}</span>
      }
      return (
        <select
          value={r.approval||''}
          onChange={async e => {
            const val = e.target.value
            if (val === 'Updates Needed') { setRevisionText(r.revision_notes||''); setRevisionPrompt(r); return }
            try { await api.updateSection('funnel', r.id, { approval: val }); softReload() }
            catch(err) { toast.error(err.message) }
          }}
          onClick={e => e.stopPropagation()}
          style={{
            background:'var(--bg3)',border:'1px solid var(--border2)',borderRadius:6,
            color: colorMap[r.approval]||'var(--text3)',
            padding:'4px 8px',fontSize:11,fontWeight:700,cursor:'pointer',
            outline:'none',width:'100%',fontFamily:'DM Sans,sans-serif'
          }}
        >
          <option value="">— —</option>
          {['Pending','Approved','Updates Needed'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      )
    }},
    { key:'approved_by', label:'Approved By', render: r => r.approved_by
      ? <span style={{fontSize:11,color:'var(--text2)'}}>{r.approved_by}{r.approved_at ? <span style={{color:'var(--text3)',display:'block',fontSize:10}}>{new Date(r.approved_at).toLocaleDateString()}</span> : null}</span>
      : <span style={{color:'var(--text3)'}}>—</span>
    },
    { key:'revision_notes', label:'Revision Notes', render: r => <span className="td-wrap">{r.revision_notes||'—'}</span> },
    { key:'audit_report', label:'Audit Report', render: r => r.audit_report
      ? <span style={{color:'var(--accent3)',fontSize:11,fontWeight:600,cursor:'pointer'}} onClick={()=>openAudit(r)}>✓ View Report</span>
      : <span style={{color:'var(--text3)',fontSize:11}}>—</span>
    },
  ].filter(c => isVisible(c.key))

  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%'}}>
      <div className="toolbar">
        <div className="toolbar-left">
          <span className="toolbar-title">⬡ Funnel Building</span>
          {myMarket && <MarketBadge market={myMarket} />}
          <span className="toolbar-sub">{rows.length} records</span>
        </div>
        <div style={{display:'flex',gap:8}}>
          <DensityToggle />
          <FilterButton groups={FUNNEL_FILTERS} {...funnelFilter} />
          <ColumnPicker section="funnel" cols={colVis} toggle={colToggle} reset={colReset} />
          {cFunnel('create') && <button className="btn btn-primary" onClick={openAdd}>+ Add Record</button>}
        </div>
      </div>
      <div className="table-wrap" style={{flex:1,overflow:'auto'}}>
        {loading ? <Empty icon="⏳" title="Loading..." /> : rows.length === 0
          ? <Empty icon="⬡" title="No funnel records" sub="Add a product first — records auto-create per market" />
          : <table className="data-table"><thead>
              <tr>
                {cols.map((c, ci) => (
                  <th key={c.key} {...getThProps(c.key, ci, cols)}
                    onClick={() => { if(sortCol===c.key){setSortDir(d=>d==='asc'?'desc':'asc')}else{setSortCol(c.key);setSortDir('asc')} }}
                    style={{...getThProps(c.key, ci, cols).style, cursor:'pointer', userSelect:'none'}}>
                    {c.label}
                    {sortCol===c.key && <span style={{marginLeft:4,fontSize:9}}>{sortDir==='asc'?'▲':'▼'}</span>}
                    <Resizer colKey={c.key} index={ci}/>
                  </th>
                ))}
                <th style={{width:160,textAlign:'center',position:'sticky',right:0,zIndex:5,background:'var(--bg3)',borderLeft:'2px solid var(--border2)'}}>Actions</th>
              </tr>

            </thead>
            <tbody>{[...rows].filter(r => funnelFilter.matches(r, {})).sort((a,b) => {
              if (!sortCol) return 0
              const av = String(a[sortCol]||'').toLowerCase()
              const bv = String(b[sortCol]||'').toLowerCase()
              return sortDir==='asc' ? av.localeCompare(bv) : bv.localeCompare(av)
            }).map((r,ri) => (
              <tr key={r.id} {...rowProps(r.id)} className={[rowProps(r.id).className, isNewlyAssigned(r.assigned_at)?"row-new":""].filter(Boolean).join(" ")||undefined}>
                {cols.map((c, ci) => <td key={c.key} {...getTdProps(c.key, ci, cols, ri)}>{c.render(r)}</td>)}
                <td style={{position:'sticky',right:0,zIndex:2,background: ri%2===0 ? 'var(--bg2)' : 'var(--bg)',borderLeft:'2px solid var(--border2)',padding:'6px 8px'}}>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:4,width:148}}>
                    {cFunnel('view') && <button className="btn btn-secondary" style={{padding:'4px 6px',fontSize:11,whiteSpace:'nowrap',justifyContent:'center'}} onClick={() => setViewRow(r)}>👁 View</button>}
                    {cFunnel('edit') && <button className="btn btn-secondary" style={{padding:'4px 6px',fontSize:11,whiteSpace:'nowrap',justifyContent:'center'}} onClick={() => openEdit(r)}>✏️ Edit</button>}
                    {cFunnel('audit') ? (
                      <button className="btn" style={{padding:'4px 6px',fontSize:11,whiteSpace:'nowrap',justifyContent:'center',background:'linear-gradient(135deg,rgba(139,92,246,0.2),rgba(59,130,246,0.2))',border:'1px solid rgba(139,92,246,0.4)',color:'#A78BFA'}} onClick={() => openAudit(r)}>🔍 Audit</button>
                    ) : <span/>}
                    {cFunnel('wetracked') ? (
                      <button className="btn" style={{padding:'4px 6px',fontSize:11,whiteSpace:'nowrap',justifyContent:'center',background:'rgba(16,185,129,0.15)',border:'1px solid rgba(16,185,129,0.3)',color:'#34D399'}} onClick={() => verifyWetracked(r)}>⚡ WT</button>
                    ) : <span/>}
                    {cFunnel('checklist') ? (
                      <div style={{display:'flex',flexDirection:'column',gap:2}}>
                        <button className="btn" style={{padding:'4px 6px',fontSize:11,whiteSpace:'nowrap',justifyContent:'center',background:'rgba(245,158,11,0.15)',border:'1px solid rgba(245,158,11,0.3)',color:'#FCD34D',width:'100%'}} onClick={() => openChecklist(r)}>
                          {(() => {
                            const allItems = CHECKLIST_ITEMS.flatMap(s => s.items)
                            const cl = typeof r.checklist === 'string' ? JSON.parse(r.checklist || '{}') : (r.checklist || {})
                            const done = allItems.filter(i => cl[i.id]).length
                            const pct = Math.round((done / allItems.length) * 100)
                            return pct === 100 ? '✅ Done' : `☑ ${pct}%`
                          })()}
                        </button>
                        {(() => {
                          const allItems = CHECKLIST_ITEMS.flatMap(s => s.items)
                          const cl = typeof r.checklist === 'string' ? JSON.parse(r.checklist || '{}') : (r.checklist || {})
                          const done = allItems.filter(i => cl[i.id]).length
                          const pct = Math.round((done / allItems.length) * 100)
                          return (
                            <div style={{height:3,background:'var(--border)',borderRadius:2,overflow:'hidden'}}>
                              <div style={{height:'100%',width:`${pct}%`,background: pct===100 ? 'var(--accent3)' : 'var(--gold)',borderRadius:2,transition:'width 0.3s'}}/>
                            </div>
                          )
                        })()}
                      </div>
                    ) : <span/>}
                    {cFunnel('delete') ? (
                      <button className="btn btn-danger" style={{padding:'4px 6px',fontSize:11,whiteSpace:'nowrap',justifyContent:'center'}} onClick={() => delRow(r)}>🗑 Del</button>
                    ) : <span/>}
                  </div>
                </td>
              </tr>
            ))}</tbody>
          </table>}
      </div>

      <ViewFunnel row={viewRow} onClose={() => setViewRow(null)} />
      {revisionPrompt && <Modal title="Send Back for Revision" onClose={() => { setRevisionPrompt(null); setRevisionText('') }}
        footer={<><button className="btn btn-ghost" onClick={() => { setRevisionPrompt(null); setRevisionText('') }}>Cancel</button>
          <button className="btn btn-primary" disabled={!revisionText.trim()} onClick={async () => {
            try { await api.updateSection('funnel', revisionPrompt.id, { approval: 'Updates Needed', revision_notes: revisionText.trim() }); setRevisionPrompt(null); setRevisionText(''); softReload() }
            catch(err){ toast.error(err.message) }
          }}>Send Back for Revision</button></>}>
        <div style={{fontSize:13,color:'var(--text2)',marginBottom:12}}>Explain what needs to change. The funnel builder will see this note.</div>
        <Textarea label="Revision Notes (required)" value={revisionText} onChange={e => setRevisionText(e.target.value)} style={{minHeight:120}} autoFocus />
      </Modal>}

      {/* Checklist Modal */}
      {checklistRow && (
        <Modal title={`☑ Checklist — ${checklistRow.product_name} (${checklistRow.market})`} onClose={() => setChecklistRow(null)}
          footer={
            <div style={{display:'flex',gap:8,alignItems:'center',width:'100%'}}>
              <button className="btn btn-ghost" onClick={() => setChecklistRow(null)}>Close</button>
              <div style={{flex:1}}/>
              <button className="btn btn-primary" onClick={saveChecklist} disabled={checklistSaving}>
                {checklistSaving ? 'Saving...' : '💾 Save Checklist'}
              </button>
            </div>
          }>

          {/* Progress bar */}
          {(() => {
            const allItems = CHECKLIST_ITEMS.flatMap(s => s.items)
            const done = allItems.filter(i => checklistData[i.id]).length
            const pct = Math.round((done / allItems.length) * 100)
            return (
              <div style={{marginBottom:20}}>
                <div style={{display:'flex',justifyContent:'space-between',marginBottom:6}}>
                  <span style={{fontSize:12,fontWeight:600,color:'var(--text2)'}}>Progress</span>
                  <span style={{fontSize:12,fontWeight:700,fontFamily:'DM Mono,monospace',color: pct===100 ? 'var(--accent3)' : 'var(--text)'}}>
                    {done}/{allItems.length} — {pct}%
                  </span>
                </div>
                <div style={{height:6,background:'var(--border)',borderRadius:3,overflow:'hidden'}}>
                  <div style={{height:'100%',width:`${pct}%`,background: pct===100 ? 'var(--accent3)' : 'var(--accent)',borderRadius:3,transition:'width 0.3s'}}/>
                </div>
                {pct===100 && (
                  <div style={{marginTop:8,padding:'8px 12px',background:'rgba(16,185,129,0.1)',border:'1px solid rgba(16,185,129,0.25)',borderRadius:8,fontSize:12,color:'var(--accent3)',fontWeight:600,textAlign:'center'}}>
                    ✅ All items completed — ready to mark as Ready!
                  </div>
                )}
              </div>
            )
          })()}

          {/* Checklist sections */}
          {CHECKLIST_ITEMS.map(section => (
            <div key={section.section} style={{marginBottom:18}}>
              <div style={{display:'flex',alignItems:'center',gap:8,padding:'8px 0',borderBottom:'1px solid var(--border)',marginBottom:10}}>
                <span style={{fontSize:15}}>{section.icon}</span>
                <span style={{fontSize:12,fontWeight:700,color:'var(--text)',letterSpacing:-0.2}}>{section.section}</span>
              </div>
              {section.note && (
                <div style={{fontSize:11,color:'var(--text3)',fontFamily:'DM Mono,monospace',marginBottom:10,padding:'6px 10px',background:'var(--bg3)',borderRadius:6,lineHeight:1.5}}>
                  {section.note}
                </div>
              )}
              {section.items.map(item => (
                <div key={item.id} onClick={() => toggleCheck(item.id)}
                  style={{
                    display:'flex',alignItems:'flex-start',gap:10,padding:'8px 10px',
                    borderRadius:8,marginBottom:4,cursor: 'pointer',
                    background: checklistData[item.id] ? 'rgba(16,185,129,0.06)' : 'transparent',
                    border: `1px solid ${checklistData[item.id] ? 'rgba(16,185,129,0.2)' : 'transparent'}`,
                    transition:'all 0.15s'
                  }}>
                  <div style={{
                    width:18,height:18,borderRadius:5,border:`2px solid ${checklistData[item.id] ? 'var(--accent3)' : 'var(--border2)'}`,
                    background: checklistData[item.id] ? 'var(--accent3)' : 'transparent',
                    flexShrink:0,marginTop:1,display:'flex',alignItems:'center',justifyContent:'center',
                    transition:'all 0.15s'
                  }}>
                    {checklistData[item.id] && <span style={{fontSize:11,color:'#000',fontWeight:700,lineHeight:1}}>✓</span>}
                  </div>
                  <span style={{
                    fontSize:12,lineHeight:1.5,
                    color: checklistData[item.id] ? 'var(--text2)' : 'var(--text)',
                    textDecoration: checklistData[item.id] ? 'line-through' : 'none',
                    transition:'all 0.15s'
                  }}>{item.label}</span>
                </div>
              ))}
            </div>
          ))}


        </Modal>
      )}

      {/* Wetracked Verify Modal */}
      {wetrackedRow && (
        <Modal title={`⚡ Wetracked Verification — ${wetrackedRow.product_name} (${wetrackedRow.market})`} onClose={() => { setWetrackedRow(null); setWetrackedResult(null) }}
          footer={<button className="btn btn-ghost" onClick={() => { setWetrackedRow(null); setWetrackedResult(null) }}>Close</button>}>

          <div style={{background:'var(--bg3)',borderRadius:8,padding:'10px 14px',marginBottom:14}}>
            <div style={{fontSize:10,color:'var(--text3)',fontFamily:'DM Mono,monospace',textTransform:'uppercase',letterSpacing:'0.6px',marginBottom:2}}>Testing URL</div>
            <div style={{fontSize:12,color:'var(--accent)',wordBreak:'break-all'}}>
              {wetrackedRow.funnel_link}?wetracked.io=connected
            </div>
          </div>

          {wetrackedLoading && (
            <div style={{textAlign:'center',padding:'30px 0'}}>
              <div style={{width:32,height:32,border:'3px solid var(--border2)',borderTopColor:'var(--accent3)',borderRadius:'50%',animation:'spin 0.7s linear infinite',margin:'0 auto 12px'}}/>
              <div style={{fontSize:13,color:'var(--text2)'}}>Launching headless browser...</div>
              <div style={{fontSize:11,color:'var(--text3)',marginTop:6}}>Loading page and waiting for Wetracked alert (up to 20s)</div>
            </div>
          )}

          {wetrackedResult && !wetrackedLoading && (
            <div>
              <div style={{
                padding:'16px 20px', borderRadius:10, marginBottom:14, textAlign:'center',
                background: wetrackedResult.connected ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
                border: `1px solid ${wetrackedResult.connected ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}`,
              }}>
                <div style={{fontSize:28,marginBottom:8}}>{wetrackedResult.connected ? '✅' : '❌'}</div>
                <div style={{fontSize:14,fontWeight:700,color: wetrackedResult.connected ? 'var(--accent3)' : 'var(--red)'}}>
                  {wetrackedResult.connected ? 'Wetracked is Connected' : 'Wetracked Not Detected'}
                </div>
                <div style={{fontSize:12,color:'var(--text2)',marginTop:6}}>{wetrackedResult.message}</div>
              </div>

              {wetrackedResult.alertText && (
                <div style={{background:'var(--bg3)',borderRadius:8,padding:'10px 14px',marginBottom:10}}>
                  <div style={{fontSize:10,color:'var(--text3)',fontFamily:'DM Mono,monospace',textTransform:'uppercase',letterSpacing:'0.6px',marginBottom:4}}>Alert Text Received</div>
                  <div style={{fontSize:13,color:'var(--text)',fontFamily:'DM Mono,monospace'}}>"{wetrackedResult.alertText}"</div>
                </div>
              )}

              {wetrackedResult.error && (
                <div style={{background:'rgba(239,68,68,0.08)',borderRadius:8,padding:'10px 14px',border:'1px solid rgba(239,68,68,0.2)'}}>
                  <div style={{fontSize:10,color:'var(--text3)',fontFamily:'DM Mono,monospace',textTransform:'uppercase',letterSpacing:'0.6px',marginBottom:4}}>Error Details</div>
                  <div style={{fontSize:12,color:'var(--red)',fontFamily:'DM Mono,monospace'}}>{wetrackedResult.error}</div>
                </div>
              )}
            </div>
          )}

          {!wetrackedLoading && !wetrackedResult && (
            <div style={{textAlign:'center',padding:'20px 0',color:'var(--text3)'}}>
              <div style={{fontSize:11}}>Verification will launch automatically</div>
            </div>
          )}
        </Modal>
      )}

      {/* Audit Modal */}
      {auditRow && (
        <Modal title={`🔍 Audit — ${auditRow.product_name} (${auditRow.market})`} onClose={() => setAuditRow(null)}
          footer={
            <div style={{display:'flex',gap:8,alignItems:'center',width:'100%'}}>
              <button className="btn btn-ghost" onClick={() => setAuditRow(null)}>Close</button>
              <div style={{flex:1}}/>
              {(auditText || auditRow?.audit_report) && (
                <button className="btn btn-danger btn-sm" onClick={clearAudit}>🗑 Clear Report</button>
              )}
              {auditText && <button className="btn btn-primary" onClick={saveAudit}>💾 Save Report</button>}
            </div>
          }>
          {/* Funnel link info */}
          <div style={{background:'var(--bg3)',borderRadius:8,padding:'10px 14px',marginBottom:14,display:'flex',alignItems:'center',justifyContent:'space-between',gap:10}}>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:10,color:'var(--text3)',fontFamily:'DM Mono,monospace',textTransform:'uppercase',letterSpacing:'0.6px',marginBottom:2}}>Sales / Funnel URL</div>
              <div style={{fontSize:12,color:auditRow.funnel_link?'var(--accent)':'var(--text3)',wordBreak:'break-all'}}>{auditRow.funnel_link || 'No funnel link set'}</div>
              <div style={{fontSize:10,color:'var(--text3)',fontFamily:'DM Mono,monospace',textTransform:'uppercase',letterSpacing:'0.6px',marginTop:8,marginBottom:2}}>Advertorial URL</div>
              <div style={{fontSize:12,color:auditRow.advertorial_link?'var(--accent)':'var(--text3)',wordBreak:'break-all'}}>{auditRow.advertorial_link || '— none —'}</div>
            </div>
            <button className="btn btn-primary btn-sm" onClick={() => runAudit()} disabled={auditLoading||!auditRow.funnel_link}>
              {auditLoading ? '⏳ Auditing...' : '🔍 Run Audit'}
            </button>
          </div>

          {/* Manual content paste fallback */}
          {showManual && (
            <div style={{marginBottom:14}}>
              <div style={{fontSize:12,color:'var(--gold)',fontWeight:600,marginBottom:6}}>⚠ Page could not be fetched. Paste the page content manually:</div>
              <textarea
                value={manualContent}
                onChange={e => setManualContent(e.target.value)}
                placeholder="Paste your funnel page text content here..."
                style={{width:'100%',minHeight:120,background:'var(--bg3)',border:'1px solid var(--border2)',borderRadius:8,color:'var(--text)',padding:'10px 12px',fontSize:12,fontFamily:'DM Mono,monospace',resize:'vertical'}}
              />
              <button className="btn btn-primary btn-sm" style={{marginTop:8}} onClick={() => runAudit(manualContent)} disabled={auditLoading||!manualContent.trim()}>
                {auditLoading ? '⏳ Auditing...' : '🔍 Run Audit with Pasted Content'}
              </button>
            </div>
          )}

          {/* Audit result */}
          {auditText && (
            <div>
              <div style={{fontSize:10,fontWeight:700,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'0.8px',fontFamily:'DM Mono,monospace',marginBottom:8}}>Audit Report</div>
              <textarea
                value={auditText}
                onChange={e => setAuditText(e.target.value)}
                style={{width:'100%',minHeight:400,background:'var(--bg3)',border:'1px solid var(--border2)',borderRadius:8,color:'var(--text)',padding:'12px 14px',fontSize:12,fontFamily:'DM Mono,monospace',lineHeight:1.7,resize:'vertical'}}
              />
              <div style={{fontSize:11,color:'var(--text3)',marginTop:6}}>You can edit the report before saving.</div>
            </div>
          )}

          {!auditText && !auditLoading && !showManual && (
            <div style={{textAlign:'center',padding:'30px 0',color:'var(--text3)'}}>
              <div style={{fontSize:32,marginBottom:8}}>🔍</div>
              <div style={{fontSize:13}}>Click "Run Audit" to analyse the funnel page</div>
            </div>
          )}
          {auditLoading && (
            <div style={{textAlign:'center',padding:'30px 0'}}>
              <div style={{width:32,height:32,border:'3px solid var(--border2)',borderTopColor:'var(--accent)',borderRadius:'50%',animation:'spin 0.7s linear infinite',margin:'0 auto 12px'}}/>
              <div style={{fontSize:13,color:'var(--text2)'}}>Claude is auditing your funnel page...</div>
            </div>
          )}
        </Modal>
      )}

      {modal && <Modal title={modal==='add'?'Add Funnel Record':'Edit Funnel Record'} onClose={() => setModal(null)}
        footer={<><button className="btn btn-ghost" onClick={() => setModal(null)}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving?'Saving...':'Save'}</button></>}>
        {isOwner && <div className="form-row">
          <Select label="Product" options={products.map(p=>({value:p.product_id,label:`${p.product_id} — ${p.product_name}`}))} value={form.product_id||''} onChange={set('product_id')} />
          <Select label="Market" options={activeMarkets} value={form.market||''} onChange={set('market')} />
        </div>}
        {/* Funnel Link — always editable (the funnel builder's one job) */}
        <Input label="Funnel Link" type="url" value={form.funnel_link||''} onChange={set('funnel_link')} />
        <Input label="Advertorial Link" type="url" value={form.advertorial_link||''} onChange={set('advertorial_link')} />
        {(isOwner || perms?.can_approve === true) && <>
        <Select label="Who Creates Funnel" options={settingsLists.who_creates} value={form.who_creates_funnel||''} onChange={set('who_creates_funnel')} />
        <Input label="Brand Name" value={form.brand_name||''} onChange={set('brand_name')} />
        <Input label="Title" value={form.title||''} onChange={set('title')} />
        <Textarea label="Benefits" value={form.benefits||''} onChange={set('benefits')} />
        <Textarea label="Paragraphs" value={form.paragraphs||''} onChange={set('paragraphs')} style={{minHeight:120}} />
        <Textarea label="Reviews" value={form.reviews||''} onChange={set('reviews')} style={{minHeight:100}} />
        <Textarea label="FAQs" value={form.faqs||''} onChange={set('faqs')} style={{minHeight:100}} />
        </>}
      </Modal>}
    </div>
  )
}

// ── CREATIVES ────────────────────────────────────────────────
export function Creatives({ market }) {
  const { user } = useAuth()
  const isOwner = user?.role === 'owner'
  const perms = (typeof user?.permissions === 'object' && user?.permissions) ? user.permissions : {}
  const cCre = (a) => can(perms, 'creatives', a, isOwner)
  const cvUsers = useUsers()
  const cvCanAssign = isOwner || perms?.can_approve === true
  const canDeadline = isOwner || perms?.can_approve === true
  const myMarket = isOwner ? market : user?.assigned_market
  const { rows, loading, reload, softReload, saveInline } = useSection('creatives', myMarket)
  const { rowProps } = useRowSelection('creatives')
  const _cvMarkets = useActiveMarkets()
  const CREATIVE_FILTERS = [
    { key:'market', label:'Market', options: _cvMarkets.map(m=>({value:m,label:m})) },
    { key:'creative_status', label:'Work Status', options: CREATIVE_STATUS.map(x=>({value:x,label:x})) },
    { key:'approval', label:'Approval', options:[{value:'Pending',label:'Pending'},{value:'Approved',label:'Approved'},{value:'Updates Needed',label:'Updates Needed'}] },
  ]
  const creativeFilter = useTableFilter('creatives', CREATIVE_FILTERS)
  const products = useProducts()
  const activeMarkets = useActiveMarkets()
  const settingsLists = useSettingsLists()
  const { sortCol, setSortCol, sortDir, setSortDir } = usePersistentSort('creatives', 'product_id')
  const [modal, setModal] = useState(null)
  const [viewRow, setViewRow] = useState(null)
  const [form, setForm] = useState({})
  const [saving, setSaving] = useState(false)
  const { cols: colVis, isVisible, toggle: colToggle, reset: colReset } = useColumns('creatives')
  const { getThProps, getTdProps, Resizer } = useResizableColumns('creatives')

  function openEdit(r) { setForm(r); setModal(r) }
  const [revisionPrompt, setRevisionPrompt] = useState(null)
  const [revisionText, setRevisionText] = useState('')
  function openAdd() { setForm({ market: myMarket||'SE', creative_status:'' }); setModal('add') }

  async function save() {
    setSaving(true)
    try {
      if (modal === 'add') await api.createSection('creatives', form)
      else await api.updateSection('creatives', modal.id, form)
      toast.success('Saved!'); setModal(null); softReload()
    } catch (err) { toast.error(err.message) }
    setSaving(false)
  }
  async function delRow(r) {
    if (!window.confirm(`Delete creative record for ${r.product_name} — ${r.market}? This cannot be undone.`)) return
    try { await api.deleteSection('creatives', r.id); toast.success('Deleted'); softReload() }
    catch (err) { toast.error(err.message) }
  }
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }))

  const cols = [
    { key:'market', label:'Market', render: r => <MarketBadge market={r.market} /> },
    { key:'product_id', label:'Product ID', render: r => <strong>{r.p_product_id||r.product_id}</strong> },
    { key:'brand_name', label:'Brand Name', render: r => r.brand_name||'—' },
    { key:'assets_status', label:'Assets', render: r => {
      const ready = r.assets_status === 'Ready'
      return <span style={{display:'flex',alignItems:'center',gap:4}}>
        <span style={{fontSize:11,fontWeight:700,color:ready?'var(--accent3)':'#F59E0B',whiteSpace:'nowrap'}}>{ready?'✓ Ready':'⏳ Awaiting'}</span>
        {ready && r.assets_link ? <a href={/^https?:\/\//.test(r.assets_link) ? r.assets_link : 'https://'+r.assets_link} target="_blank" rel="noreferrer" style={{color:'var(--accent)',fontSize:11}}>↗</a> : null}
      </span>
    }},
    { key:'competitor_product_url', label:'Comp Link', render: r => <LinkCell url={r.competitor_product_url} /> },
    { key:'aliexpress_link', label:'Sourcing Link', render: r => <LinkCell url={r.aliexpress_link} /> },
    { key:'competitor_winning_ad_links', label:'Winning Ad Links', render: r => {
      const links = (r.competitor_winning_ad_links||'').split('\n').map(l=>l.trim().replace(/^"|"$/g,'')).filter(Boolean)
      if (!links.length) return <span>—</span>
      return <div style={{display:'flex',flexDirection:'column',gap:3}}>
        {links.map((url,i)=>url.startsWith('http')
          ? <a key={i} href={url} target="_blank" rel="noopener noreferrer" style={{color:'#1E6FBF',fontSize:11,textDecoration:'none'}} onMouseEnter={e=>e.target.style.textDecoration='underline'} onMouseLeave={e=>e.target.style.textDecoration='none'}>🔗 Ad {i+1}</a>
          : <span key={i} style={{fontSize:11,color:'var(--text2)'}}>{url}</span>
        )}
      </div>
    }},
    { key:'competitor_ad_creative_link', label:'Comp Creative', render: r => <LinkCell url={r.competitor_ad_creative_link} /> },
    { key:'creative_file_link', label:'Creative File', render: r => <LinkCell url={r.creative_file_link} /> },
    { key:'deadline', label:'Deadline', render: r => {
      const overdue = r.deadline && r.creative_status !== 'Ready' && new Date(r.deadline) < new Date(new Date().toISOString().slice(0,10))
      return <span style={{display:'flex',alignItems:'center',gap:4}}>
        <input type="date" value={r.deadline?r.deadline.slice(0,10):''} disabled={!canDeadline}
          onChange={async e => { try { await saveInline(r.id, { deadline: e.target.value }) } catch(err){ toast.error(err.message) } }}
          onClick={e=>e.stopPropagation()}
          style={{background:'var(--bg3)',border:'1px solid '+(overdue?'#EF4444':'var(--border2)'),borderRadius:6,color:overdue?'#EF4444':'var(--text)',padding:'3px 6px',fontSize:11,outline:'none'}} />
        {overdue?'⚠':''}
      </span>
    }},
    { key:'creative_status', label:'Work Status', render: r => (
      <select value={r.creative_status||'Not Started'} onChange={async e => {
        try { await api.updateSection('creatives',r.id,{creative_status:e.target.value}); softReload() }
        catch(err){toast.error(err.message)}
      }} onClick={e=>e.stopPropagation()} style={{background:'var(--bg3)',border:'1px solid var(--border2)',borderRadius:6,color:r.creative_status==='Updates Required'?'#F59E0B':'var(--text)',padding:'4px 8px',fontSize:11,fontWeight:600,cursor:'pointer',outline:'none',width:'100%',fontFamily:'DM Sans,sans-serif'}}>
        {['Not Started','In Progress','Editing','Revision','Ready','Updates Required','On Hold','Cancelled'].map(s=><option key={s} value={s}>{s}</option>)}
      </select>
    )},
    { key:'assigned_to', label:'Assignee', render: r => cvCanAssign ? (<>
      <select value={r.assigned_to||''} onChange={async e => { try { await api.updateSection('creatives', r.id, { assigned_to: e.target.value || null }); softReload() } catch(err){ toast.error(err.message) } }}
        onClick={e=>e.stopPropagation()} style={{background:'var(--bg3)',border:'1px solid var(--border2)',borderRadius:6,color:'var(--text)',padding:'4px 8px',fontSize:11,cursor:'pointer',outline:'none',width:'100%'}}>
        <option value="">— Unassigned —</option>
        {cvUsers.map(u=><option key={u.id} value={u.id}>{u.full_name}</option>)}
      </select>{isNewlyAssigned(r.assigned_at) && <span style={{marginLeft:4,fontSize:9,fontWeight:700,color:'#F59E0B'}}>● NEW</span>}</>
    ) : <span style={{fontSize:11,color:r.assignee_name?'var(--text2)':'var(--text3)'}}>{r.assignee_name||'—'}{isNewlyAssigned(r.assigned_at) && <span style={{marginLeft:6,fontSize:9,fontWeight:700,color:'#F59E0B'}}>● NEW</span>}</span> },
    { key:'completed', label:'Completed', render: r => (r.completed_by || r.date_completed)
      ? <span style={{fontSize:11,color:'var(--text2)'}}>{r.completed_by||'—'}{r.date_completed ? <span style={{color:'var(--text3)',display:'block',fontSize:10}}>{new Date(r.date_completed).toLocaleDateString()}</span> : null}</span>
      : <span style={{color:'var(--text3)'}}>—</span>
    },
    { key:'approval', label:'Approval', render: r => {
      const canApp = isOwner || perms.can_approve === true
      const colorMap = { 'Approved':'var(--accent3)', 'Updates Needed':'#F59E0B', 'Pending':'var(--text3)' }
      if (!canApp) return <span style={{fontSize:11,fontWeight:700,color:colorMap[r.approval]||'var(--text3)'}}>{r.approval||'—'}</span>
      return (
        <select value={r.approval||''} onClick={e=>e.stopPropagation()}
          onChange={async e => { const val = e.target.value; if (val === 'Updates Needed') { setRevisionText(r.revision_notes||''); setRevisionPrompt(r); return } try { await api.updateSection('creatives', r.id, { approval: val }); softReload() } catch(err){ toast.error(err.message) } }}
          style={{background:'var(--bg3)',border:'1px solid var(--border2)',borderRadius:6,color:colorMap[r.approval]||'var(--text3)',padding:'4px 8px',fontSize:11,fontWeight:700,cursor:'pointer',outline:'none',width:'100%',fontFamily:'DM Sans,sans-serif'}}>
          <option value="">— —</option>
          {['Pending','Approved','Updates Needed'].map(st=><option key={st} value={st}>{st}</option>)}
        </select>
      )
    }},
    { key:'approved_by', label:'Approved By', render: r => r.approved_by
      ? <span style={{fontSize:11,color:'var(--text2)'}}>{r.approved_by}{r.approved_at ? <span style={{color:'var(--text3)',display:'block',fontSize:10}}>{new Date(r.approved_at).toLocaleDateString()}</span> : null}</span>
      : <span style={{color:'var(--text3)'}}>—</span>
    },
    { key:'revision_notes', label:'Revision Notes', render: r => <span className="td-wrap">{r.revision_notes||'—'}</span> },
  ].filter(c => isVisible(c.key))

  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%'}}>
      <div className="toolbar">
        <div className="toolbar-left">
          <span className="toolbar-title">🎨 Creatives</span>
          {myMarket && <MarketBadge market={myMarket} />}
          <span className="toolbar-sub">{rows.length} records</span>
        </div>
        <div style={{display:'flex',gap:8}}>
          <DensityToggle />
          <FilterButton groups={CREATIVE_FILTERS} {...creativeFilter} />
          <ColumnPicker section="creatives" cols={colVis} toggle={colToggle} reset={colReset} />
          {isOwner && <button className="btn btn-primary" onClick={openAdd}>+ Add Record</button>}
        </div>
      </div>
      <div className="table-wrap" style={{flex:1,overflow:'auto'}}>
        {loading ? <Empty icon="⏳" title="Loading..." /> : rows.length === 0
          ? <Empty icon="🎨" title="No creative records" />
          : <table className="data-table"><thead>
              <tr>
                {cols.map((c, ci) => (
                  <th key={c.key} {...getThProps(c.key, ci, cols)}
                    onClick={() => { if(sortCol===c.key){setSortDir(d=>d==='asc'?'desc':'asc')}else{setSortCol(c.key);setSortDir('asc')} }}
                    style={{...getThProps(c.key, ci, cols).style, cursor:'pointer', userSelect:'none'}}>
                    {c.label}
                    {sortCol===c.key && <span style={{marginLeft:4,fontSize:9}}>{sortDir==='asc'?'▲':'▼'}</span>}
                    <Resizer colKey={c.key} index={ci}/>
                  </th>
                ))}
                <th style={{width:170,textAlign:'center',position:'sticky',right:0,zIndex:6,background:'var(--bg3)',borderLeft:'2px solid var(--border2)'}}>Actions</th>
              </tr>

            </thead>
            <tbody>{[...rows].filter(r => creativeFilter.matches(r, {})).sort((a,b) => {
              if (!sortCol) return 0
              const av = String(a[sortCol]||'').toLowerCase()
              const bv = String(b[sortCol]||'').toLowerCase()
              return sortDir==='asc' ? av.localeCompare(bv) : bv.localeCompare(av)
            }).map((r, ri) => (
              <tr key={r.id} {...rowProps(r.id)} className={[rowProps(r.id).className, isNewlyAssigned(r.assigned_at)?"row-new":""].filter(Boolean).join(" ")||undefined}>
                {cols.map((c, ci) => <td key={c.key} {...getTdProps(c.key, ci, cols, ri)}>{c.render(r)}</td>)}
                <td style={{position:'sticky',right:0,zIndex:2,background:'var(--bg2)',borderLeft:'2px solid var(--border2)',padding:'6px 8px'}}><ActionBtns onView={() => setViewRow(r)} onEdit={() => openEdit(r)} onDel={() => delRow(r)} canView={cCre('view')} canEdit={cCre('edit')} canDel={cCre('delete')} /></td>
              </tr>
            ))}</tbody>
          </table>}
      </div>
      <ViewCreative row={viewRow} onClose={() => setViewRow(null)} />
      {revisionPrompt && <Modal title="Send Back for Revision" onClose={() => { setRevisionPrompt(null); setRevisionText('') }}
        footer={<><button className="btn btn-ghost" onClick={() => { setRevisionPrompt(null); setRevisionText('') }}>Cancel</button>
          <button className="btn btn-primary" disabled={!revisionText.trim()} onClick={async () => {
            try { await api.updateSection('creatives', revisionPrompt.id, { approval: 'Updates Needed', revision_notes: revisionText.trim() }); setRevisionPrompt(null); setRevisionText(''); softReload() }
            catch(err){ toast.error(err.message) }
          }}>Send Back for Revision</button></>}>
        <div style={{fontSize:13,color:'var(--text2)',marginBottom:12}}>Explain what needs to change. The editor will see this note.</div>
        <Textarea label="Revision Notes (required)" value={revisionText} onChange={e => setRevisionText(e.target.value)} style={{minHeight:120}} autoFocus />
      </Modal>}
      {modal && <Modal title={modal==='add'?'Add Creative Record':'Edit Creative Record'} onClose={() => setModal(null)}
        footer={<><button className="btn btn-ghost" onClick={() => setModal(null)}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving?'Saving...':'Save'}</button></>}>
        {isOwner && <div className="form-row">
          <Select label="Product" options={products.map(p=>({value:p.product_id,label:`${p.product_id} — ${p.product_name}`}))} value={form.product_id||''} onChange={set('product_id')} />
          <Select label="Market" options={activeMarkets} value={form.market||''} onChange={set('market')} />
        </div>}
        <Input label="Creative File/Link (Drive)" type="url" value={form.creative_file_link||''} onChange={set('creative_file_link')} />
        {isOwner && <>
        <div className="form-row">
          <Select label="Work Status" options={['Not Started','In Progress','Editing','Revision','Ready','Updates Required','On Hold','Cancelled']} value={form.creative_status||'Not Started'} onChange={set('creative_status')} />
          <Select label="Assigned Editor" options={['', ...settingsLists.editors]} value={form.assigned_editor||''} onChange={set('assigned_editor')} />
        </div>
        <Textarea label="Revision Notes" value={form.revision_notes||''} onChange={set('revision_notes')} />
        </>}
      </Modal>}
    </div>
  )
}

// ── AD COPY ──────────────────────────────────────────────────
export function AdCopy({ market }) {
  const { user } = useAuth()
  const isOwner = user?.role === 'owner'
  const perms = (typeof user?.permissions === 'object' && user?.permissions) ? user.permissions : {}
  const cAdc = (a) => can(perms, 'adcopy', a, isOwner)
  const acUsers = useUsers()
  const acCanAssign = isOwner || perms?.can_approve === true
  const canDeadline = isOwner || perms?.can_approve === true
  const myMarket = isOwner ? market : user?.assigned_market
  const { rows, loading, reload, softReload, saveInline } = useSection('adcopy', myMarket)
  const { rowProps } = useRowSelection('adcopy')
  const _acMarkets = useActiveMarkets()
  const ADCOPY_FILTERS = [
    { key:'market', label:'Market', options: _acMarkets.map(m=>({value:m,label:m})) },
    { key:'status', label:'Work Status', options: AD_COPY_STATUS.map(x=>({value:x,label:x})) },
    { key:'approval', label:'Approval', options:[{value:'Pending',label:'Pending'},{value:'Approved',label:'Approved'},{value:'Updates Needed',label:'Updates Needed'}] },
  ]
  const adcopyFilter = useTableFilter('adcopy', ADCOPY_FILTERS)
  const products = useProducts()
  const activeMarkets = useActiveMarkets()
  const settingsLists = useSettingsLists()
  const { sortCol, setSortCol, sortDir, setSortDir } = usePersistentSort('adcopy', 'product_id')
  const [modal, setModal] = useState(null)
  const [viewRow, setViewRow] = useState(null)
  const [form, setForm] = useState({})
  const [saving, setSaving] = useState(false)
  const { cols: colVis, isVisible, toggle: colToggle, reset: colReset } = useColumns('adcopy')
  const { getThProps, getTdProps, Resizer } = useResizableColumns('adcopy')

  function openEdit(r) { setForm(r); setModal(r) }
  const [revisionPrompt, setRevisionPrompt] = useState(null)
  const [revisionText, setRevisionText] = useState('')
  function openAdd() { setForm({ market: myMarket||'SE', status:'' }); setModal('add') }

  async function save() {
    setSaving(true)
    try {
      if (modal === 'add') await api.createSection('adcopy', form)
      else await api.updateSection('adcopy', modal.id, form)
      toast.success('Saved!'); setModal(null); softReload()
    } catch (err) { toast.error(err.message) }
    setSaving(false)
  }
  async function delRow(r) {
    if (!window.confirm(`Delete ad copy record for ${r.product_name} — ${r.market}? This cannot be undone.`)) return
    try { await api.deleteSection('adcopy', r.id); toast.success('Deleted'); softReload() }
    catch (err) { toast.error(err.message) }
  }
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }))
  const [adapting, setAdapting] = useState(false)

  const CURRENCY_MAP = {
    'SE':'SEK','UK':'GBP','NO':'NOK','DK':'DKK',
    'NL-BE':'EUR','GE':'EUR','FR':'EUR','US':'USD',
    'AU':'AUD','CA':'CAD','FI':'EUR'
  }
  const CURRENCY_RATE = {
    'SEK':11.5,'GBP':0.85,'NOK':11.5,'DKK':7.5,
    'EUR':1,'USD':1.08,'AUD':1.65,'CAD':1.47
  }

  async function adaptCompetitorCopy() {
    if (!form.competitor_ad_copy_primary && !form.competitor_ad_copy_headline) {
      toast.error('No competitor copy found for this record'); return
    }
    setAdapting(true)
    try {
      const currency = CURRENCY_MAP[form.market] || 'EUR'
      const rate = CURRENCY_RATE[currency] || 1
      const price = form.selling_price_eur
        ? (parseFloat(form.selling_price_eur) * rate).toFixed(0)
        : null

      const token = localStorage.getItem('adops_token')
      const r = await fetch('/api/content/proxy-claude', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          system: `You are an expert Facebook ad copy adapter. You take competitor ad copy and rewrite it as ours — same persuasive power, but compliant with Meta's 2026 advertising policies and in the target market's language.

CRITICAL: competitor copy often BREAKS Meta policy. Do not copy their violations. Rewrite them.

ALWAYS DO:
- Replace their brand/product name with our brand
- Replace prices/currency with ours
- Replace country/cultural references with our target market
- Write the ENTIRE output in the target market's language (translate naturally, not word-for-word)
- Keep the same structure, story arc, tone and persuasion technique

ALWAYS STRIP AND REWRITE these Meta violations if present in the source:
1. PERSONAL ATTRIBUTES — any line asserting/implying the reader's condition.
   "Leiden Sie unter Fersensporn?" / "Do you suffer from X?" / "Struggling with X?"
   -> restate as a general truth or a third-person story: "Heel pain is more common than most people realise."
2. DISEASE CLAIMS — treat/cure/prevent/heal/eliminate/reverse + a condition.
   -> structure/function only: "formulated to support healthy circulation"
3. GUARANTEES / SPECIFIC OUTCOMES / TIMELINES — "80% relief in 3 days", "guaranteed".
   -> soften: "many people notice a difference over a few weeks"
4. BEFORE/AFTER OR TRANSFORMATION framing -> remove the transformation, keep the story
5. NEGATIVE SELF-PERCEPTION / shame hooks -> reframe onto what people gain
6. SHOCK / GORE / SENSATIONALISM (amputation, blood, extreme suffering) -> keep the emotional stakes, drop the shock
7. EXPERT ENDORSEMENT OF OUTCOMES — "Doctors recommend", "I'm a doctor and I tell patients", "Proctologists discovered"
   -> a personal story with no credential endorsing a health outcome

Keep the length similar to the source (do not shorten a long advertorial into a short ad).
Headline under 40 characters.
Return valid JSON only.`,
          user: `Adapt this competitor ad copy for our product.

COMPETITOR AD COPY:
Primary Text: ${form.competitor_ad_copy_primary || ''}
Headline: ${form.competitor_ad_copy_headline || ''}

OUR DETAILS:
Brand Name: ${form.brand_name || 'our brand'}
Our Price: ${price ? price + ' ' + currency : 'keep original price structure'}
Market: ${form.market}
Language to write in: ${{ 'SE':'Swedish','UK':'English','NL-BE':'Dutch','GE':'German','FR':'French','NO':'Norwegian','DK':'Danish','FI':'Finnish','US':'English','AU':'English','CA':'English' }[form.market] || 'English'}
Currency to use: ${currency}
Target Country: ${{ 'SE':'Sweden','UK':'United Kingdom','NL-BE':'Netherlands/Belgium','GE':'Germany','FR':'France','NO':'Norway','DK':'Denmark','FI':'Finland','US':'United States','AU':'Australia','CA':'Canada' }[form.market] || form.market}

RULES:
- STRIP AND REWRITE any Meta policy violation from the source (personal attributes / "do you suffer from" framing, disease claims, guarantees, specific outcomes or timelines, before-after, shame hooks, shock, expert endorsement). Keep the persuasion, lose the violation.
- WRITE THE ENTIRE ADAPTED COPY IN ${{ 'SE':'Swedish','UK':'English','NL-BE':'Dutch','GE':'German','FR':'French','NO':'Norwegian','DK':'Danish','FI':'Finnish','US':'English','AU':'English','CA':'English' }[form.market] || 'English'} — both the primary text and the headline. If the competitor copy is in another language, translate it naturally (not word-for-word) into that language.
- Replace ALL mentions of their brand/product name with our Brand Name
- Replace ALL prices and currency symbols with our price in ${currency}
- Replace ANY country-specific references with our target country (e.g. "German doctor" → "${{ 'SE':'Swedish','UK':'British','NL-BE':'Dutch','GE':'German','FR':'French','NO':'Norwegian','DK':'Danish','FI':'Finnish','US':'American','AU':'Australian','CA':'Canadian' }[form.market] || ''} doctor", "trusted in Germany" → "trusted in ${{ 'SE':'Sweden','UK':'United Kingdom','NL-BE':'Netherlands','GE':'Germany','FR':'France','NO':'Norway','DK':'Denmark','FI':'Finland','US':'United States','AU':'Australia','CA':'Canada' }[form.market] || form.market}")
- Replace ANY cultural references, local celebrities, or region-specific language to match our target market
- Keep the exact same tone, structure and persuasion technique
- Keep Facebook compliance (no guarantees, no before/after)
- Keep the primary text the SAME LENGTH as the competitor copy (do not shorten a long advertorial into a short ad)
- Headline must stay under 40 characters

Return ONLY these XML tags, nothing else. Do not use JSON. Do not escape anything — write the copy exactly as it should appear, including line breaks:
<PRIMARY>
the adapted primary text
</PRIMARY>
<HEADLINE>
the adapted headline, under 40 characters
</HEADLINE>
<NOTES>
what you changed for Meta compliance and why (or "none" if the source was already clean)
</NOTES>`,
          max_tokens: 16000
        })
      })
      const d = await r.json()
      if (!r.ok || d.error) {
        const msg = typeof d.error === 'string' ? d.error : JSON.stringify(d.error || 'Request failed')
        throw new Error(msg)
      }
      if (!d.text) throw new Error('Empty response from AI')
      const text = d.text
      // XML tags survive truncation and need no escaping — same proven method as translate
      const grab = (tag) => {
        const m = text.match(new RegExp('<' + tag + '>([\\s\\S]*?)</' + tag + '>', 'i'))
        return m ? m[1].trim() : ''
      }
      const primary = grab('PRIMARY')
      const headline = grab('HEADLINE')
      const notes = grab('NOTES')
      if (!primary && !headline) {
        throw new Error('AI response could not be read: ' + text.slice(0, 120))
      }
      if (notes && notes.toLowerCase() !== 'none') console.info('Adapt compliance notes:', notes)
      setForm(f => ({
        ...f,
        adapted_copy_primary: primary,
        adapted_copy_headline: headline
      }))
      toast.success('Competitor copy adapted successfully!')
    } catch (err) {
      const m = typeof err === 'string' ? err : (err.message || JSON.stringify(err))
      toast.error('Adapt failed: ' + m)
    }
    setAdapting(false)
  }

  const cols = [
    { key:'market', label:'Market', render: r => <MarketBadge market={r.market} /> },
    { key:'product_id', label:'Product ID', render: r => r.p_product_id||r.product_id },
    { key:'brand_name', label:'Brand', render: r => r.brand_name||'—' },
    { key:'funnel_link', label:'Funnel Link', render: r => <LinkCell url={r.funnel_link} /> },
    { key:'competitor_ad_copy_primary', label:'Comp Primary', render: r => <CopyableText text={r.competitor_ad_copy_primary} /> },
    { key:'competitor_ad_copy_headline', label:'Comp Headline', render: r => <CopyableText text={r.competitor_ad_copy_headline} /> },
    { key:'ai_copy_primary', label:'AI Primary', render: r => <CopyableText text={r.ai_copy_primary} /> },
    { key:'ai_copy_headline', label:'AI Headline', render: r => <CopyableText text={r.ai_copy_headline} /> },
    { key:'adapted_copy_primary', label:'Adapted Primary', render: r => <CopyableText text={r.adapted_copy_primary} /> },
    { key:'adapted_copy_headline', label:'Adapted Headline', render: r => <CopyableText text={r.adapted_copy_headline} /> },
    { key:'deadline', label:'Deadline', render: r => {
      const overdue = r.deadline && r.status !== 'Ready' && new Date(r.deadline) < new Date(new Date().toISOString().slice(0,10))
      return <span style={{display:'flex',alignItems:'center',gap:4}}>
        <input type="date" value={r.deadline?r.deadline.slice(0,10):''} disabled={!canDeadline}
          onChange={async e => { try { await saveInline(r.id, { deadline: e.target.value }) } catch(err){ toast.error(err.message) } }}
          onClick={e=>e.stopPropagation()}
          style={{background:'var(--bg3)',border:'1px solid '+(overdue?'#EF4444':'var(--border2)'),borderRadius:6,color:overdue?'#EF4444':'var(--text)',padding:'3px 6px',fontSize:11,outline:'none'}} />
        {overdue?'⚠':''}
      </span>
    }},
    { key:'status', label:'Work Status', render: r => (
      <select value={r.status||'Not Started'} onChange={async e => {
        try { await api.updateSection('adcopy',r.id,{status:e.target.value}); softReload() }
        catch(err){toast.error(err.message)}
      }} onClick={e=>e.stopPropagation()} style={{background:'var(--bg3)',border:'1px solid var(--border2)',borderRadius:6,color:r.status==='Updates Required'?'#F59E0B':'var(--text)',padding:'4px 8px',fontSize:11,fontWeight:600,cursor:'pointer',outline:'none',width:'100%',fontFamily:'DM Sans,sans-serif'}}>
        {['Not Started','In Progress','Writing','Revision','Ready','Updates Required','On Hold','Cancelled'].map(s=><option key={s} value={s}>{s}</option>)}
      </select>
    )},
    { key:'assigned_to', label:'Assignee', render: r => acCanAssign ? (<>
      <select value={r.assigned_to||''} onChange={async e => { try { await api.updateSection('adcopy', r.id, { assigned_to: e.target.value || null }); softReload() } catch(err){ toast.error(err.message) } }}
        onClick={e=>e.stopPropagation()} style={{background:'var(--bg3)',border:'1px solid var(--border2)',borderRadius:6,color:'var(--text)',padding:'4px 8px',fontSize:11,cursor:'pointer',outline:'none',width:'100%'}}>
        <option value="">— Unassigned —</option>
        {acUsers.map(u=><option key={u.id} value={u.id}>{u.full_name}</option>)}
      </select>{isNewlyAssigned(r.assigned_at) && <span style={{marginLeft:4,fontSize:9,fontWeight:700,color:'#F59E0B'}}>● NEW</span>}</>
    ) : <span style={{fontSize:11,color:r.assignee_name?'var(--text2)':'var(--text3)'}}>{r.assignee_name||'—'}{isNewlyAssigned(r.assigned_at) && <span style={{marginLeft:6,fontSize:9,fontWeight:700,color:'#F59E0B'}}>● NEW</span>}</span> },
    { key:'completed', label:'Completed', render: r => (r.completed_by || r.date_completed)
      ? <span style={{fontSize:11,color:'var(--text2)'}}>{r.completed_by||'—'}{r.date_completed ? <span style={{color:'var(--text3)',display:'block',fontSize:10}}>{new Date(r.date_completed).toLocaleDateString()}</span> : null}</span>
      : <span style={{color:'var(--text3)'}}>—</span>
    },
    { key:'approval', label:'Approval', render: r => {
      const canApp = isOwner || perms.can_approve === true
      const colorMap = { 'Approved':'var(--accent3)', 'Updates Needed':'#F59E0B', 'Pending':'var(--text3)' }
      if (!canApp) return <span style={{fontSize:11,fontWeight:700,color:colorMap[r.approval]||'var(--text3)'}}>{r.approval||'—'}</span>
      return (
        <select value={r.approval||''} onClick={e=>e.stopPropagation()}
          onChange={async e => { const val = e.target.value; if (val === 'Updates Needed') { setRevisionText(r.revision_notes||''); setRevisionPrompt(r); return } try { await api.updateSection('adcopy', r.id, { approval: val }); softReload() } catch(err){ toast.error(err.message) } }}
          style={{background:'var(--bg3)',border:'1px solid var(--border2)',borderRadius:6,color:colorMap[r.approval]||'var(--text3)',padding:'4px 8px',fontSize:11,fontWeight:700,cursor:'pointer',outline:'none',width:'100%',fontFamily:'DM Sans,sans-serif'}}>
          <option value="">— —</option>
          {['Pending','Approved','Updates Needed'].map(st=><option key={st} value={st}>{st}</option>)}
        </select>
      )
    }},
    { key:'approved_by', label:'Approved By', render: r => r.approved_by
      ? <span style={{fontSize:11,color:'var(--text2)'}}>{r.approved_by}{r.approved_at ? <span style={{color:'var(--text3)',display:'block',fontSize:10}}>{new Date(r.approved_at).toLocaleDateString()}</span> : null}</span>
      : <span style={{color:'var(--text3)'}}>—</span>
    },
    { key:'revision_notes', label:'Revision Notes', render: r => <span className="td-wrap">{r.revision_notes||'—'}</span> },
  ].filter(c => isVisible(c.key))

  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%'}}>
      <div className="toolbar">
        <div className="toolbar-left">
          <span className="toolbar-title">✍️ Ad Copy</span>
          {myMarket && <MarketBadge market={myMarket} />}
          <span className="toolbar-sub">{rows.length} records</span>
        </div>
        <div style={{display:'flex',gap:8}}>
          <DensityToggle />
          <FilterButton groups={ADCOPY_FILTERS} {...adcopyFilter} />
          <ColumnPicker section="adcopy" cols={colVis} toggle={colToggle} reset={colReset} />
          {cAdc('create') && <button className="btn btn-primary" onClick={openAdd}>+ Add Record</button>}
        </div>
      </div>
      <div className="table-wrap" style={{flex:1,overflow:'auto'}}>
        {loading ? <Empty icon="⏳" title="Loading..." /> : rows.length === 0
          ? <Empty icon="✍️" title="No ad copy records" />
          : <table className="data-table"><thead>
              <tr>
                {cols.map((c, ci) => (
                  <th key={c.key} {...getThProps(c.key, ci, cols)}
                    onClick={() => { if(sortCol===c.key){setSortDir(d=>d==='asc'?'desc':'asc')}else{setSortCol(c.key);setSortDir('asc')} }}
                    style={{...getThProps(c.key, ci, cols).style, cursor:'pointer', userSelect:'none'}}>
                    {c.label}
                    {sortCol===c.key && <span style={{marginLeft:4,fontSize:9}}>{sortDir==='asc'?'▲':'▼'}</span>}
                    <Resizer colKey={c.key} index={ci}/>
                  </th>
                ))}
                <th style={{width:170,textAlign:'center',position:'sticky',right:0,zIndex:6,background:'var(--bg3)',borderLeft:'2px solid var(--border2)'}}>Actions</th>
              </tr>

            </thead>
            <tbody>{[...rows].filter(r => adcopyFilter.matches(r, {})).sort((a,b) => {
              if (!sortCol) return 0
              const av = String(a[sortCol]||'').toLowerCase()
              const bv = String(b[sortCol]||'').toLowerCase()
              return sortDir==='asc' ? av.localeCompare(bv) : bv.localeCompare(av)
            }).map((r, ri) => (
              <tr key={r.id} {...rowProps(r.id)} className={[rowProps(r.id).className, isNewlyAssigned(r.assigned_at)?"row-new":""].filter(Boolean).join(" ")||undefined}>
                {cols.map((c, ci) => <td key={c.key} {...getTdProps(c.key, ci, cols, ri)}>{c.render(r)}</td>)}
                <td style={{position:'sticky',right:0,zIndex:2,background:'var(--bg2)',borderLeft:'2px solid var(--border2)',padding:'6px 8px'}}><ActionBtns onView={() => setViewRow(r)} onEdit={() => openEdit(r)} onDel={() => delRow(r)} canView={cAdc('view')} canEdit={cAdc('edit')} canDel={cAdc('delete')} /></td>
              </tr>
            ))}</tbody>
          </table>}
      </div>
      <ViewAdCopy row={viewRow} onClose={() => setViewRow(null)} />
      {revisionPrompt && <Modal title="Send Back for Revision" onClose={() => { setRevisionPrompt(null); setRevisionText('') }}
        footer={<><button className="btn btn-ghost" onClick={() => { setRevisionPrompt(null); setRevisionText('') }}>Cancel</button>
          <button className="btn btn-primary" disabled={!revisionText.trim()} onClick={async () => {
            try { await api.updateSection('adcopy', revisionPrompt.id, { approval: 'Updates Needed', revision_notes: revisionText.trim() }); setRevisionPrompt(null); setRevisionText(''); softReload() }
            catch(err){ toast.error(err.message) }
          }}>Send Back for Revision</button></>}>
        <div style={{fontSize:13,color:'var(--text2)',marginBottom:12}}>Explain what needs to change. The copywriter will see this note.</div>
        <Textarea label="Revision Notes (required)" value={revisionText} onChange={e => setRevisionText(e.target.value)} style={{minHeight:120}} autoFocus />
      </Modal>}
      {modal && <Modal title={modal==='add'?'Add Ad Copy':'Edit Ad Copy'} onClose={() => setModal(null)}
        footer={<><button className="btn btn-ghost" onClick={() => setModal(null)}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving?'Saving...':'Save'}</button></>}>
        {isOwner && <div className="form-row">
          <Select label="Product" options={products.map(p=>({value:p.product_id,label:`${p.product_id} — ${p.product_name}`}))} value={form.product_id||''} onChange={set('product_id')} />
          <Select label="Market" options={activeMarkets} value={form.market||''} onChange={set('market')} />
        </div>}
        <Textarea label="AI Generated Copy — Primary Text" value={form.ai_copy_primary||''} onChange={set('ai_copy_primary')} style={{minHeight:100}} />
        <Input label="AI Generated Copy — Headline" value={form.ai_copy_headline||''} onChange={set('ai_copy_headline')} />

        {/* Adapted copy section */}
        <div style={{borderTop:'1px solid var(--border)',paddingTop:14,marginTop:4}}>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:10}}>
            <div>
              <div style={{fontSize:12,fontWeight:700,color:'var(--text)'}}>Adapted Competitor Copy</div>
              <div style={{fontSize:11,color:'var(--text3)',marginTop:2}}>Competitor copy adapted with your brand name &amp; market pricing</div>
            </div>
            <button type="button" className="btn btn-primary btn-sm"
              onClick={adaptCompetitorCopy} disabled={adapting}
              style={{background:'linear-gradient(135deg,#7C3AED,#3B82F6)',border:'none',whiteSpace:'nowrap'}}>
              {adapting ? '⏳ Adapting...' : '✨ Adapt from Competitor'}
            </button>
          </div>
          <Textarea label="Adapted Primary Text" value={form.adapted_copy_primary||''} onChange={set('adapted_copy_primary')} style={{minHeight:100}} />
          <Input label="Adapted Headline" value={form.adapted_copy_headline||''} onChange={set('adapted_copy_headline')} />
        </div>

        <div className="form-row">
          <Select label="Work Status" options={['Not Started','In Progress','Writing','Revision','Ready','Updates Required','On Hold','Cancelled']} value={form.status||'Not Started'} onChange={set('status')} />
        </div>
      </Modal>}
    </div>
  )
}

// ── AD SCHEDULING ────────────────────────────────────────────
export function AdScheduling({ market }) {
  const { user } = useAuth()
  const isOwner = user?.role === 'owner'
  const perms = (typeof user?.permissions === 'object' && user?.permissions) ? user.permissions : {}
  const cSch = (a) => can(perms, 'scheduling', a, isOwner)
  const myMarket = isOwner ? market : user?.assigned_market
  const { rows, loading, reload, softReload, saveInline } = useSection('scheduling', myMarket)
  const { rowProps } = useRowSelection('scheduling')
  const [statusFilter, setStatusFilter] = useState(() => { try { return JSON.parse(localStorage.getItem('sched_statusfilter')||'null') } catch { return null } })
  const [showArchived, setShowArchived] = useState(false)
  const [filterOpen, setFilterOpen] = useState(false)
  const SCHED_STATUSES = ['Scheduled','Active','Paused','Completed','Cancelled','']
  const activeFilter = statusFilter || SCHED_STATUSES
  const toggleStatus = (st) => setStatusFilter(prev => {
    const cur = prev || SCHED_STATUSES
    const next = cur.includes(st) ? cur.filter(x=>x!==st) : [...cur, st]
    try { localStorage.setItem('sched_statusfilter', JSON.stringify(next)) } catch {}
    return next
  })
  async function archiveRow(r) {
    try { await saveInline(r.id, { archived: !r.archived }); toast.success(r.archived?'Restored':'Archived') } catch(e){ toast.error(e.message) }
  }
  const products = useProducts()
  const activeMarkets = useActiveMarkets()
  const settingsLists = useSettingsLists()
  const [fbAccounts, setFbAccounts] = useState([])
  const [fbPages, setFbPages] = useState([])
  useEffect(() => {
    api.fbTree().then(tree => {
      const accts = [], pages = []
      for (const bm of tree) {
        for (const a of (bm.accounts||[])) accts.push(a)
        for (const p of (bm.pages||[])) pages.push(p)
      }
      setFbAccounts(accts); setFbPages(pages)
    }).catch(() => { setFbAccounts([]); setFbPages([]) })
  }, [])
  const accountsForMarket = (mkt) => {
    const list = fbAccounts.filter(a => a.market === mkt).map(a => a.account_name).filter(Boolean)
    return [...new Set(list)]
  }
  const pagesForMarket = (mkt) => {
    const list = fbPages.filter(p => p.market === mkt).map(p => p.page_name).filter(Boolean)
    return [...new Set(list)]
  }
  const { sortCol, setSortCol, sortDir, setSortDir } = usePersistentSort('scheduling', 'product_id')
  const [modal, setModal] = useState(null)
  const [viewRow, setViewRow] = useState(null)
  const [form, setForm] = useState({})
  const [saving, setSaving] = useState(false)
  const { cols: colVis, isVisible, toggle: colToggle, reset: colReset } = useColumns('scheduling')
  const { getThProps, getTdProps, Resizer } = useResizableColumns('scheduling')

  function openEdit(r) { setForm(r); setModal(r) }
  function openAdd() { setForm({ market: myMarket||'SE', campaign_status:'' }); setModal('add') }

  const MARKET_CURRENCY = { SE:'SEK', DK:'DKK', NO:'NOK', FI:'EUR', 'NL-BE':'EUR' }
  const campaignName = r => {
    // Entry price from offer_to_use. Two formats are supported:
    //  1) structured  "1x=249, 2x=449, 3x=549"
    //  2) freeform    "1 flaska – 379 kr | Köp 2 få 1 GRATIS – 599 kr"  (Pricing Agent offers)
    // For freeform we take the FIRST number attached to a currency marker, so quantities
    // like "3 flasker" are never mistaken for a price. Comma decimals (34,95) are handled.
    const offerText = r.offer_to_use || ''
    let price = null
    const structured = offerText.match(/1x\s*=\s*([\d.]+)/i)
    if (structured) {
      price = parseFloat(structured[1])
    } else if (offerText) {
      // Try, in order:
      //  a) a number right after "=" (e.g. "... = 34.99 €")  — most reliable for bundle offers
      //  b) a number attached to a currency: €34,95 / 34.95 EUR / 379 kr / 34.99 €
      // Bare quantities like "2x" or "1+1" are never matched because they aren't after "=" nor currency-tagged.
      const afterEq = offerText.match(/=\s*(\d+(?:[.,]\d{1,2})?)/)
      const money = offerText.match(/[€£$]\s*(\d+(?:[.,]\d{1,2})?)|(\d+(?:[.,]\d{1,2})?)\s*(?:kr|kč|eur|dkk|sek|nok|€)/i)
      const raw = (afterEq && afterEq[1]) || (money && (money[1] || money[2]))
      if (raw) price = parseFloat(String(raw).replace(',', '.'))
    }
    const currency = MARKET_CURRENCY[r.market] || 'EUR'
    const rates = settingsLists.currency_rates || {}
    const rate = currency === 'EUR' ? 1 : (parseFloat(rates[currency]) || null)
    let roasStr = null, profitStr = null
    if (price != null && rate) {
      // EUR costs -> market currency
      const eurCosts = (parseFloat(r.total_cogs)||0) + (parseFloat(r.total_fees)||0)
      const costs = eurCosts * rate
      const profit = price - costs
      const beRoas = profit > 0 ? (price / profit) : 0
      const sym = currency === 'EUR' ? '€' : ''
      const suffix = currency === 'EUR' ? '' : ' ' + currency
      roasStr = beRoas ? `${beRoas.toFixed(2)}x` : null
      profitStr = `${sym}${profit.toFixed(0)}${suffix}`
    }
    return [r.market, r.brand_name, roasStr, profitStr].filter(Boolean).join(' | ')
  }

  async function save() {
    setSaving(true)
    try {
      if (modal === 'add') await api.createSection('scheduling', form)
      else await api.updateSection('scheduling', modal.id, form)
      toast.success('Saved!'); setModal(null); softReload()
    } catch (err) { toast.error(err.message) }
    setSaving(false)
  }
  async function delRow(r) {
    if (!window.confirm(`Delete scheduling record for ${r.product_name} — ${r.market}? This cannot be undone.`)) return
    try { await api.deleteSection('scheduling', r.id); toast.success('Deleted'); softReload() }
    catch (err) { toast.error(err.message) }
  }
  async function delRow(r) {
    if (!window.confirm(`Delete scheduling record for ${r.product_name} — ${r.market}? This cannot be undone.`)) return
    try { await api.deleteSection('scheduling', r.id); toast.success('Deleted'); softReload() }
    catch (err) { toast.error(err.message) }
  }
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }))

  const cols = [
    { key:'market', label:'Market', render: r => <MarketBadge market={r.market} /> },
    { key:'product_id', label:'Product ID', render: r => <strong>{r.product_id}</strong> },
    { key:'ad_account', label:'Ad Account', render: r => (
      <select value={r.ad_account||''} onChange={async e => {
        try { await saveInline(r.id, { ad_account: e.target.value }) }
        catch(err){toast.error(err.message)}
      }} onClick={e=>e.stopPropagation()} style={{background:'var(--bg3)',border:'1px solid var(--border2)',borderRadius:6,color:'var(--text)',padding:'4px 8px',fontSize:11,cursor:'pointer',outline:'none',width:'100%'}}>
        <option value="">— Select —</option>
        {accountsForMarket(r.market).map(a=><option key={a} value={a}>{a}</option>)}
      </select>
    )},
    { key:'page', label:'Page', render: r => (
      <select value={r.page||''} onChange={async e => { try { await saveInline(r.id, { page: e.target.value }) } catch(err){toast.error(err.message)} }}
        onClick={e=>e.stopPropagation()} style={{background:'var(--bg3)',border:'1px solid var(--border2)',borderRadius:6,color:'var(--text)',padding:'4px 8px',fontSize:11,cursor:'pointer',outline:'none',width:'100%'}}>
        <option value="">— Select —</option>
        {pagesForMarket(r.market).map(pg=><option key={pg} value={pg}>{pg}</option>)}
      </select>
    )},
    { key:'campaign_name', label:'Campaign Name', render: r => <CopyableText text={campaignName(r)} /> },
    { key:'daily_budget_eur', label:'Budget €', render: r => r.daily_budget_eur?`€${r.daily_budget_eur}`:'—' },
    { key:'scheduled_date', label:'Date', render: r => (
      <input type="date" value={r.scheduled_date ? r.scheduled_date.split('T')[0] : ''}
        onChange={async e => {
          try { await saveInline(r.id, { scheduled_date: e.target.value||null }) }
          catch(err){toast.error(err.message)}
        }} onClick={e=>e.stopPropagation()}
        style={{background:'var(--bg3)',border:'1px solid var(--border2)',borderRadius:6,color:'var(--text)',padding:'4px 8px',fontSize:11,cursor:'pointer',outline:'none',fontFamily:'DM Mono,monospace',width:'100%'}}
      />
    )},
    { key:'advertorial_link', label:'Advertorial Link', render: r => <UrlCopyCell url={r.advertorial_link} /> },
    { key:'funnel_link', label:'Funnel Link', render: r => <LinkCell url={r.funnel_link} /> },
    { key:'creative_file_link', label:'Creative', render: r => <LinkCell url={r.creative_file_link} /> },
    { key:'ai_copy_primary', label:'AI Primary', render: r => <CopyableText text={r.ai_copy_primary} /> },
    { key:'ai_copy_headline', label:'AI Headline', render: r => <CopyableText text={r.ai_copy_headline} /> },
    { key:'adapted_copy_primary', label:'Adapted Primary', render: r => <CopyableText text={r.adapted_copy_primary} /> },
    { key:'adapted_copy_headline', label:'Adapted Headline', render: r => <CopyableText text={r.adapted_copy_headline} /> },
    { key:'brand_name', label:'Brand', render: r => r.brand_name||'—' },
    { key:'readiness', label:'Readiness', render: r => {
      const allGreen = r.product_approval === 'Approved' && r.funnel_approval === 'Approved' && r.creative_approval === 'Approved' && r.adcopy_approval === 'Approved'
      if (allGreen) return <span style={{fontSize:11,fontWeight:700,color:'var(--accent3)',whiteSpace:'nowrap'}}>✓ Ready</span>
      const notReady = []
      if (r.product_approval !== 'Approved') notReady.push('Product')
      if (r.funnel_approval !== 'Approved') notReady.push('Funnel')
      if (r.creative_approval !== 'Approved') notReady.push('Creative')
      if (r.adcopy_approval !== 'Approved') notReady.push('Ad Copy')
      return <span title={`Not approved: ${notReady.join(', ')}`} style={{fontSize:11,fontWeight:700,color:'#F59E0B',whiteSpace:'nowrap',cursor:'help'}}>⚠ Re-verify</span>
    }},
  
  ].filter(c => isVisible(c.key))

  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%'}}>
      <div className="toolbar">
        <div className="toolbar-left">
          <span className="toolbar-title">📅 Ad Scheduling</span>
          {myMarket && <MarketBadge market={myMarket} />}
          <span className="toolbar-sub">{rows.length} records</span>
        </div>
        <div style={{display:'flex',gap:8,alignItems:'center',position:'relative'}}>
          <button className={showArchived?'btn btn-primary':'btn btn-secondary'} onClick={()=>setShowArchived(a=>!a)}>
            {showArchived?'📁 Archived':'📂 Active'}
          </button>
          <div style={{position:'relative'}}>
            <button className="btn btn-secondary" onClick={()=>setFilterOpen(o=>!o)}>
              ⚑ Status{statusFilter && statusFilter.length<SCHED_STATUSES.length ? ` (${statusFilter.length})` : ''}
            </button>
            {filterOpen && <div style={{position:'absolute',top:'110%',right:0,zIndex:20,background:'var(--bg2)',border:'1px solid var(--border2)',borderRadius:8,padding:10,minWidth:170,boxShadow:'0 8px 24px rgba(0,0,0,0.4)'}}>
              {['Scheduled','Active','Paused','Completed','Cancelled'].map(st=>(
                <label key={st} style={{display:'flex',alignItems:'center',gap:8,padding:'4px 2px',fontSize:12,cursor:'pointer'}}>
                  <input type="checkbox" checked={activeFilter.includes(st)} onChange={()=>toggleStatus(st)} style={{width:14,height:14}} />
                  {st}
                </label>
              ))}
              <label style={{display:'flex',alignItems:'center',gap:8,padding:'4px 2px',fontSize:12,cursor:'pointer',color:'var(--text3)'}}>
                <input type="checkbox" checked={activeFilter.includes('')} onChange={()=>toggleStatus('')} style={{width:14,height:14}} />
                No status
              </label>
              <div style={{borderTop:'1px solid var(--border)',marginTop:6,paddingTop:6,display:'flex',gap:6}}>
                <button className="btn btn-ghost" style={{fontSize:10,padding:'2px 6px'}} onClick={()=>{setStatusFilter(null);try{localStorage.removeItem('sched_statusfilter')}catch{}}}>All</button>
              </div>
            </div>}
          </div>
          <DensityToggle />
          <ColumnPicker section="scheduling" cols={colVis} toggle={colToggle} reset={colReset} />
          {cSch('create') && <button className="btn btn-primary" onClick={openAdd}>+ Add Record</button>}
        </div>
      </div>
      <div className="table-wrap" style={{flex:1,overflow:'auto'}}>
        {loading ? <Empty icon="⏳" title="Loading..." /> : rows.length === 0
          ? <Empty icon="📅" title="No scheduling records" />
          : <table className="data-table"><thead>
              <tr>
                {cols.map((c, ci) => (
                  <th key={c.key} {...getThProps(c.key, ci, cols)}
                    onClick={() => { if(sortCol===c.key){setSortDir(d=>d==='asc'?'desc':'asc')}else{setSortCol(c.key);setSortDir('asc')} }}
                    style={{...getThProps(c.key, ci, cols).style, cursor:'pointer', userSelect:'none'}}>
                    {c.label}
                    {sortCol===c.key && <span style={{marginLeft:4,fontSize:9}}>{sortDir==='asc'?'▲':'▼'}</span>}
                    <Resizer colKey={c.key} index={ci}/>
                  </th>
                ))}
                <th style={{width:170,textAlign:'center',position:'sticky',right:0,zIndex:6,background:'var(--bg3)',borderLeft:'2px solid var(--border2)'}}>Actions</th>
              </tr>

            </thead>
            <tbody>{[...rows].filter(r => (showArchived ? r.archived : !r.archived) && activeFilter.includes(r.campaign_status||'')).sort((a,b) => {
              if (!sortCol) return 0
              const av = String(a[sortCol]||'').toLowerCase()
              const bv = String(b[sortCol]||'').toLowerCase()
              return sortDir==='asc' ? av.localeCompare(bv) : bv.localeCompare(av)
            }).map((r, ri) => (
              <tr key={r.id} {...rowProps(r.id)}>
                {cols.map((c, ci) => <td key={c.key} {...getTdProps(c.key, ci, cols, ri)}>{c.render(r)}</td>)}
                <td style={{position:'sticky',right:0,zIndex:2,background:'var(--bg2)',borderLeft:'2px solid var(--border2)',padding:'6px 8px'}}>
                  {(() => {
                    const CS = {
                      'Active':    { fg:'#10B981', bg:'rgba(16,185,129,0.12)',  bd:'rgba(16,185,129,0.45)' },
                      'Scheduled': { fg:'#3B82F6', bg:'rgba(59,130,246,0.12)',  bd:'rgba(59,130,246,0.45)' },
                      'Paused':    { fg:'#F59E0B', bg:'rgba(245,158,11,0.12)',  bd:'rgba(245,158,11,0.45)' },
                      'Completed': { fg:'#6B7280', bg:'rgba(107,114,128,0.12)', bd:'rgba(107,114,128,0.4)' },
                      'Cancelled': { fg:'#EF4444', bg:'rgba(239,68,68,0.12)',   bd:'rgba(239,68,68,0.45)' },
                    }
                    const c = CS[r.campaign_status] || { fg:'var(--text3)', bg:'var(--bg3)', bd:'var(--border2)' }
                    return (
                    <select value={r.campaign_status||''} onChange={async e => {
                      try { await saveInline(r.id, { campaign_status: e.target.value }) }
                      catch(err){toast.error(err.message)}
                    }} onClick={e=>e.stopPropagation()} style={{background:c.bg,border:'1px solid '+c.bd,borderRadius:6,color:c.fg,padding:'3px 6px',fontSize:10,fontWeight:700,cursor:'pointer',outline:'none',width:'100%',marginBottom:5}}>
                      <option value="" style={{background:'var(--bg3)',color:'var(--text)'}}>— Status —</option>
                      {['Scheduled','Active','Paused','Completed','Cancelled'].map(s=><option key={s} value={s} style={{background:'var(--bg3)',color:'var(--text)'}}>{s}</option>)}
                    </select>
                    )
                  })()}
                  <ActionBtns onView={() => setViewRow(r)} onEdit={() => openEdit(r)} onDel={() => delRow(r)} canView={cSch('view')} canEdit={cSch('edit')} canDel={cSch('delete')} />
                  {cSch('edit') && <button onClick={()=>archiveRow(r)} title={r.archived?'Restore':'Archive'} style={{marginTop:4,width:'100%',fontSize:10,padding:'3px',background:'var(--bg3)',border:'1px solid var(--border2)',borderRadius:6,color:'var(--text3)',cursor:'pointer'}}>{r.archived?'↩ Restore':'📁 Archive'}</button>}
                </td>
              </tr>
            ))}</tbody>
          </table>}
      </div>
      <ViewScheduling row={viewRow} onClose={() => setViewRow(null)} />
      {modal && <Modal title={modal==='add'?'Add Scheduling':'Edit Scheduling'} onClose={() => setModal(null)}
        footer={<><button className="btn btn-ghost" onClick={() => setModal(null)}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving?'Saving...':'Save'}</button></>}>
        {isOwner && <div className="form-row">
          <Select label="Product" options={products.map(p=>({value:p.product_id,label:`${p.product_id} — ${p.product_name}`}))} value={form.product_id||''} onChange={set('product_id')} />
          <Select label="Market" options={activeMarkets} value={form.market||''} onChange={set('market')} />
        </div>}
        <div className="form-row">
          <Select label="Ad Account" options={accountsForMarket(form.market)} value={form.ad_account||''} onChange={set('ad_account')} />
          <Select label="Page" options={pagesForMarket(form.market)} value={form.page||''} onChange={set('page')} />
          <Input label="Daily Budget (€)" type="number" step="0.01" value={form.daily_budget_eur||''} onChange={set('daily_budget_eur')} />
        </div>
        <div className="form-row">
          <Input label="Scheduled Date" type="date" value={form.scheduled_date||''} onChange={set('scheduled_date')} />
          <Select label="Campaign Status" options={['', ...CAMPAIGN_STATUS]} value={form.campaign_status||''} onChange={set('campaign_status')} />
        </div>
      </Modal>}
    </div>
  )
}

export function CreativeRequests({ market }) {
  const { user } = useAuth()
  const canDeadline = user?.role === 'owner' || (typeof user?.permissions === 'object' && user?.permissions?.can_approve === true)
  const isOwner = user?.role === 'owner'
  const perms = (typeof user?.permissions === 'object' && user?.permissions) ? user.permissions : {}
  const cReq = (a) => can(perms, 'creativerequests', a, isOwner)
  const myMarket = isOwner ? market : user?.assigned_market
  const { rows, loading, reload, softReload, saveInline } = useSection('creative-requests', myMarket)
  const { rowProps } = useRowSelection('creativerequests')
  const products = useProducts()
  const activeMarkets = useActiveMarkets()
  const settingsLists = useSettingsLists()
  const { sortCol, setSortCol, sortDir, setSortDir } = usePersistentSort('creativerequests', 'priority')
  const [modal, setModal] = useState(null)
  const [viewRow, setViewRow] = useState(null)
  const [form, setForm] = useState({})
  const [saving, setSaving] = useState(false)
  const { cols: colVis, isVisible, toggle: colToggle, reset: colReset } = useColumns('creativerequests')
  const { getThProps, getTdProps, Resizer } = useResizableColumns('creativerequests')

  function openEdit(r) { setForm(r); setModal(r) }
  function openAdd() { setForm({ market: myMarket||'SE', creative_type:'Video', priority:'Normal', status:'Requested', quantity:1 }); setModal('add') }
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }))

  async function save() {
    setSaving(true)
    try {
      if (modal === 'add') await api.createSection('creative-requests', { ...form, requested_by: user?.full_name || user?.email })
      else await api.updateSection('creative-requests', modal.id, form)
      toast.success('Saved!'); setModal(null); softReload()
    } catch (err) { toast.error(err.message) }
    setSaving(false)
  }
  async function delRow(r) {
    if (!window.confirm('Delete this creative request? This cannot be undone.')) return
    try { await api.deleteSection('creative-requests', r.id); toast.success('Deleted'); softReload() }
    catch (err) { toast.error(err.message) }
  }

  const prColor = { 'High':'#EF4444', 'Normal':'var(--text)', 'Low':'var(--text3)' }
  const stColor = { 'Delivered':'var(--accent3)', 'In Progress':'#F59E0B', 'On Hold':'var(--text3)', 'Cancelled':'var(--text3)', 'Requested':'var(--text)' }

  const cols = [
    { key:'market', label:'Market', render: r => <MarketBadge market={r.market} /> },
    { key:'product_id', label:'Product ID', render: r => <strong>{r.p_product_id||r.product_id}</strong> },
    { key:'brand_name', label:'Brand', render: r => r.brand_name||'—' },
    { key:'reference_ad_link', label:'Winning Ad Ref', render: r => <LinkCell url={r.reference_ad_link} /> },
    { key:'creative_type', label:'Type', render: r => (
      <select value={r.creative_type||'Video'} onClick={e=>e.stopPropagation()}
        onChange={async e=>{ try{ await saveInline(r.id, { creative_type: e.target.value }) }catch(err){toast.error(err.message)} }}
        style={{background:'var(--bg3)',border:'1px solid var(--border2)',borderRadius:6,color:'var(--text)',padding:'4px 8px',fontSize:11,cursor:'pointer',outline:'none',width:'100%'}}>
        {['Video','Image','Both'].map(t=><option key={t} value={t}>{t}</option>)}
      </select>
    )},
    { key:'quantity', label:'Qty', render: r => <span style={{fontWeight:600}}>{r.quantity||1}</span> },
    { key:'brief', label:'Brief', render: r => <span className="td-wrap" style={{width:'100%',display:'block'}}>{r.brief||'—'}</span> },
    { key:'priority', label:'Priority', render: r => (
      <select value={r.priority||'Normal'} onClick={e=>e.stopPropagation()}
        onChange={async e=>{ try{ await saveInline(r.id, { priority: e.target.value }) }catch(err){toast.error(err.message)} }}
        style={{background:'var(--bg3)',border:'1px solid var(--border2)',borderRadius:6,color:prColor[r.priority]||'var(--text)',padding:'4px 8px',fontSize:11,fontWeight:700,cursor:'pointer',outline:'none',width:'100%'}}>
        {['Low','Normal','High'].map(t=><option key={t} value={t}>{t}</option>)}
      </select>
    )},
    { key:'deadline', label:'Deadline', render: r => {
      const overdue = r.deadline && r.status !== 'Delivered' && new Date(r.deadline) < new Date(new Date().toISOString().slice(0,10))
      return <span style={{display:'flex',alignItems:'center',gap:4}}>
        <input type="date" value={r.deadline?r.deadline.slice(0,10):''} disabled={!canDeadline}
          onClick={e=>e.stopPropagation()}
          onChange={async e => { try { await saveInline(r.id, { deadline: e.target.value }) } catch(err){ toast.error(err.message) } }}
          style={{background:'var(--bg3)',border:'1px solid '+(overdue?'#EF4444':'var(--border2)'),borderRadius:6,color:overdue?'#EF4444':'var(--text)',padding:'3px 6px',fontSize:11,outline:'none'}} />
        {overdue?'⚠':''}
      </span>
    }},
    { key:'status', label:'Status', render: r => (
      <select value={r.status||'Requested'} onClick={e=>e.stopPropagation()}
        onChange={async e=>{ try{ await saveInline(r.id, { status: e.target.value }) }catch(err){toast.error(err.message)} }}
        style={{background:'var(--bg3)',border:'1px solid var(--border2)',borderRadius:6,color:stColor[r.status]||'var(--text)',padding:'4px 8px',fontSize:11,fontWeight:700,cursor:'pointer',outline:'none',width:'100%'}}>
        {['Requested','In Progress','Delivered','On Hold','Cancelled'].map(t=><option key={t} value={t}>{t}</option>)}
      </select>
    )},
    { key:'assigned_editor', label:'Editor', render: r => (
      <select value={r.assigned_editor||''} onClick={e=>e.stopPropagation()}
        onChange={async e=>{ try{ await saveInline(r.id, { assigned_editor: e.target.value }) }catch(err){toast.error(err.message)} }}
        style={{background:'var(--bg3)',border:'1px solid var(--border2)',borderRadius:6,color:'var(--text)',padding:'4px 8px',fontSize:11,cursor:'pointer',outline:'none',width:'100%'}}>
        <option value="">— Select —</option>
        {settingsLists.editors.map(ed=><option key={ed} value={ed}>{ed}</option>)}
      </select>
    )},
    { key:'delivered_link', label:'Delivered Link', render: r => <LinkCell url={r.delivered_link} /> },
    { key:'requested_by', label:'Requested By', render: r => <span style={{fontSize:11,color:'var(--text2)'}}>{r.requested_by||'—'}</span> },
  ].filter(c => isVisible(c.key))

  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%'}}>
      <div className="toolbar">
        <div className="toolbar-left">
          <span className="toolbar-title">🎬 Creative Requests</span>
          {myMarket && <MarketBadge market={myMarket} />}
          <span className="toolbar-sub">{rows.length} requests</span>
        </div>
        <div style={{display:'flex',gap:8}}>
          <DensityToggle />
          <ColumnPicker section="creativerequests" cols={colVis} toggle={colToggle} reset={colReset} />
          {cReq('create') && <button className="btn btn-primary" onClick={openAdd}>+ New Request</button>}
        </div>
      </div>
      <div className="table-wrap" style={{flex:1,overflow:'auto'}}>
        {loading ? <Empty icon="⏳" title="Loading..." /> : rows.length === 0
          ? <Empty icon="🎬" title="No creative requests" sub="Raise a request from Winning Products, or click New Request." />
          : <table className="data-table"><thead>
              <tr>
                {cols.map((c, ci) => (
                  <th key={c.key} {...getThProps(c.key, ci, cols)}
                    onClick={() => { if(sortCol===c.key){setSortDir(d=>d==='asc'?'desc':'asc')}else{setSortCol(c.key);setSortDir('asc')} }}
                    style={{...getThProps(c.key, ci, cols).style, cursor:'pointer', userSelect:'none'}}>
                    {c.label}
                    {sortCol===c.key && <span style={{marginLeft:4,fontSize:9}}>{sortDir==='asc'?'▲':'▼'}</span>}
                    <Resizer colKey={c.key} index={ci}/>
                  </th>
                ))}
                <th style={{width:170,textAlign:'center',position:'sticky',right:0,zIndex:6,background:'var(--bg3)',borderLeft:'2px solid var(--border2)'}}>Actions</th>
              </tr>
            </thead>
            <tbody>{[...rows].sort((a,b) => {
              if (!sortCol) return 0
              const av = String(a[sortCol]||'').toLowerCase()
              const bv = String(b[sortCol]||'').toLowerCase()
              return sortDir==='asc' ? av.localeCompare(bv) : bv.localeCompare(av)
            }).map((r, ri) => (
              <tr key={r.id} {...rowProps(r.id)}>
                {cols.map((c, ci) => <td key={c.key} {...getTdProps(c.key, ci, cols, ri)}>{c.render(r)}</td>)}
                <td style={{position:'sticky',right:0,zIndex:2,background:'var(--bg2)',borderLeft:'2px solid var(--border2)',padding:'6px 8px'}}>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:4,width:148}}>
                    {cReq('edit') && <button className="btn btn-secondary" style={{padding:'4px 6px',fontSize:11,whiteSpace:'nowrap',justifyContent:'center'}} onClick={()=>openEdit(r)}>✏️ Edit</button>}
                    {cReq('delete') && <button className="btn btn-danger" style={{padding:'4px 6px',fontSize:11,whiteSpace:'nowrap',justifyContent:'center'}} onClick={()=>delRow(r)}>🗑 Del</button>}
                  </div>
                </td>
              </tr>
            ))}</tbody>
          </table>}
      </div>
      {modal && <Modal title={modal==='add'?'New Creative Request':'Edit Creative Request'} onClose={() => setModal(null)}
        footer={<><button className="btn btn-ghost" onClick={() => setModal(null)}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving?'Saving...':'Save'}</button></>}>
        {isOwner && <>
        <div className="form-row">
          <Select label="Product" options={products.map(p=>({value:p.product_id,label:`${p.product_id} — ${p.product_name}`}))} value={form.product_id||''} onChange={set('product_id')} />
          <Select label="Market" options={activeMarkets} value={form.market||''} onChange={set('market')} />
        </div>
        <Input label="Winning Ad Reference Link" type="url" value={form.reference_ad_link||''} onChange={set('reference_ad_link')} />
        <div className="form-row">
          <Select label="Type" options={['Video','Image','Both']} value={form.creative_type||'Video'} onChange={set('creative_type')} />
          <Input label="Quantity" type="number" min="1" value={form.quantity||1} onChange={set('quantity')} />
        </div>
        <div className="form-row">
          <Select label="Priority" options={['Low','Normal','High']} value={form.priority||'Normal'} onChange={set('priority')} />
          <Select label="Status" options={['Requested','In Progress','Delivered','On Hold','Cancelled']} value={form.status||'Requested'} onChange={set('status')} />
        </div>
        {canDeadline && <Input label="Deadline" type="date" value={form.deadline?form.deadline.slice(0,10):''} onChange={set('deadline')} />}
        <Textarea label="Brief / Angle Notes" value={form.brief||''} onChange={set('brief')} style={{minHeight:80}} />
        <Select label="Assigned Editor" options={['', ...EDITORS]} value={form.assigned_editor||''} onChange={set('assigned_editor')} />
        </>}
        <Input label="Delivered Link" type="url" value={form.delivered_link||''} onChange={set('delivered_link')} />
      </Modal>}
    </div>
  )
}

// ── ASSET PREP ────────────────────────────────────────────────
export function AssetPrep({ market }) {
  const { user } = useAuth()
  const isOwner = user?.role === 'owner'
  const perms = (typeof user?.permissions === 'object' && user?.permissions) ? user.permissions : {}
  const cAsset = (a) => can(perms, 'assetprep', a, isOwner)
  const canDeadline = isOwner || perms?.can_approve === true
  const myMarket = isOwner ? market : user?.assigned_market
  const { rows, loading, reload, softReload, saveInline } = useSection('assetprep', myMarket)
  const { rowProps } = useRowSelection('assetprep')
  const products = useProducts()
  const activeMarkets = useActiveMarkets()
  const { sortCol, setSortCol, sortDir, setSortDir } = usePersistentSort('assetprep', 'product_id')
  const [modal, setModal] = useState(null)
  const [form, setForm] = useState({})
  const [saving, setSaving] = useState(false)
  const { cols: colVis, isVisible, toggle: colToggle, reset: colReset } = useColumns('assetprep')
  const { getThProps, getTdProps, Resizer } = useResizableColumns('assetprep')
  function openEdit(r) { setForm(r); setModal(r) }
  function openAdd() { setForm({ market: myMarket||'SE', status:'Awaiting' }); setModal('add') }

  async function save() {
    setSaving(true)
    try {
      const body = { ...form }
      if (body.logo_link && body.logo_link.trim() && body.status !== 'Ready') {
        body.status = 'Ready'
        body.date_completed = new Date().toISOString().slice(0,10)
        body.completed_by = user?.full_name || ''
      }
      if (modal === 'add') await api.createSection('assetprep', body)
      else await api.updateSection('assetprep', modal.id, body)
      toast.success('Saved!'); setModal(null); softReload()
    } catch (err) { toast.error(err.message) }
    setSaving(false)
  }
  async function delRow(r) {
    if (!window.confirm(`Delete asset prep record for product ${r.product_id} — ${r.market}?`)) return
    try { await api.deleteSection('assetprep', r.id); toast.success('Deleted'); softReload() }
    catch (err) { toast.error(err.message) }
  }
  const set = k => e => setForm(f => ({ ...f, [k]: e?.target ? e.target.value : e }))

  const isOverdue = r => r.deadline && r.status !== 'Ready' && new Date(r.deadline) < new Date(new Date().toISOString().slice(0,10))

  const cols = [
    { key:'market', label:'Market', render: r => <MarketBadge market={r.market} /> },
    { key:'product_id', label:'Product ID', render: r => <strong>{r.product_id}</strong> },
    { key:'brand_name', label:'Brand', render: r => r.brand_name||'—' },
    { key:'logo_link', label:'Assets Link', render: r => r.logo_link ? <a href={/^https?:\/\//.test(r.logo_link) ? r.logo_link : 'https://'+r.logo_link} target="_blank" rel="noreferrer" style={{color:'var(--accent)'}}>Assets ↗</a> : <span style={{color:'var(--text3)'}}>—</span> },
    { key:'deadline', label:'Deadline', render: r => (
      <span style={{display:'flex',alignItems:'center',gap:4}}>
        <input type="date" value={r.deadline?r.deadline.slice(0,10):''} disabled={!canDeadline}
          onChange={async e => { try { await saveInline(r.id, { deadline: e.target.value }) } catch(err){ toast.error(err.message) } }}
          onClick={e=>e.stopPropagation()}
          style={{background:'var(--bg3)',border:'1px solid '+(isOverdue(r)?'#EF4444':'var(--border2)'),borderRadius:6,color:isOverdue(r)?'#EF4444':'var(--text)',padding:'3px 6px',fontSize:11,outline:'none'}} />
        {isOverdue(r)?'⚠':''}
      </span>
    )},
    { key:'status', label:'Status', render: r => <span style={{fontSize:11,fontWeight:700,color:r.status==='Ready'?'var(--accent3)':'#F59E0B'}}>{r.status==='Ready'?'✓ Ready':'⏳ Awaiting'}</span> },
    { key:'completed_by', label:'Completed By', render: r => <span style={{fontSize:12}}>{r.completed_by||'—'}</span> },
    { key:'date_completed', label:'Date', render: r => <span style={{fontSize:12}}>{r.date_completed?r.date_completed.slice(0,10):'—'}</span> },
  ].filter(c => isVisible(c.key))

  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%'}}>
      <div className="toolbar">
        <div className="toolbar-left">
          <span className="toolbar-title">🎨 Asset Prep</span>
          {myMarket && <MarketBadge market={myMarket} />}
          <span className="toolbar-sub">{rows.length} records</span>
        </div>
        <div style={{display:'flex',gap:8}}>
          <DensityToggle />
          <ColumnPicker section="assetprep" cols={colVis} toggle={colToggle} reset={colReset} />
          {(isOwner || cAsset('create')) && <button className="btn btn-primary" onClick={openAdd}>+ Add Record</button>}
        </div>
      </div>
      <div className="table-wrap" style={{flex:1,overflow:'auto'}}>
        {loading ? <Empty icon="⏳" title="Loading..." />
          : rows.length === 0 ? <Empty icon="🎨" title="No asset prep records" sub="Records auto-create when a product is approved." />
          : <table className="data-table"><thead>
              <tr>
                {cols.map((c, ci) => (
                  <th key={c.key} {...getThProps(c.key, ci, cols)}
                    onClick={() => { if(sortCol===c.key){setSortDir(d=>d==='asc'?'desc':'asc')}else{setSortCol(c.key);setSortDir('asc')} }}
                    style={{...getThProps(c.key, ci, cols).style, cursor:'pointer', userSelect:'none'}}>
                    {c.label}
                    {sortCol===c.key && <span style={{marginLeft:4,fontSize:9}}>{sortDir==='asc'?'▲':'▼'}</span>}
                    <Resizer colKey={c.key} index={ci}/>
                  </th>
                ))}
                <th style={{width:120,textAlign:'center',position:'sticky',right:0,zIndex:6,background:'var(--bg3)',borderLeft:'2px solid var(--border2)'}}>Actions</th>
              </tr>
            </thead>
            <tbody>{[...rows].sort((a,b) => {
              if (!sortCol) return 0
              let av = a[sortCol], bv = b[sortCol]
              if (sortCol === 'product_id') { av = Number(av)||0; bv = Number(bv)||0; return sortDir==='asc' ? av-bv : bv-av }
              av = String(av||'').toLowerCase(); bv = String(bv||'').toLowerCase()
              return sortDir==='asc' ? av.localeCompare(bv) : bv.localeCompare(av)
            }).map((r, ri) => (
              <tr key={r.id} {...rowProps(r.id)}>
                {cols.map((c, ci) => <td key={c.key} {...getTdProps(c.key, ci, cols, ri)}>{c.render(r)}</td>)}
                <td style={{position:'sticky',right:0,zIndex:2,background:'var(--bg2)',borderLeft:'2px solid var(--border2)',padding:'6px 8px',textAlign:'center'}}>
                  {(isOwner || cAsset('edit')) && <button className="btn btn-secondary" style={{padding:'4px 8px',fontSize:11}} onClick={()=>openEdit(r)}>✏️</button>}
                  {(isOwner || cAsset('delete')) && <button className="btn btn-danger" style={{padding:'4px 8px',fontSize:11,marginLeft:4}} onClick={()=>delRow(r)}>🗑</button>}
                </td>
              </tr>
            ))}</tbody>
          </table>}
      </div>
      {modal && <Modal title={modal==='add'?'Add Asset Prep Record':'Edit Asset Prep Record'} onClose={()=>setModal(null)}
        footer={<><button className="btn btn-ghost" onClick={()=>setModal(null)}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving?'Saving...':'Save'}</button></>}>
        {isOwner && <div className="form-row">
          <Select label="Product" options={products.map(p=>({value:p.product_id,label:`${p.product_id} — ${p.product_name}`}))} value={form.product_id||''} onChange={set('product_id')} />
          <Select label="Market" options={activeMarkets} value={form.market||''} onChange={set('market')} />
        </div>}
        <Input label="Assets Link (logo + product image folder)" type="url" value={form.logo_link||''} onChange={set('logo_link')} />
        {canDeadline && <Input label="Deadline" type="date" value={form.deadline?form.deadline.slice(0,10):''} onChange={set('deadline')} />}
        {isOwner && <Select label="Status" options={['Awaiting','Ready']} value={form.status||'Awaiting'} onChange={set('status')} />}
      </Modal>}
    </div>
  )
}
